function escapeHtml(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function wantsAgent(text) {
  return /\b(autonomous(?:\s+ai)?\s+agent|ai agent|ai assistant)\b/i.test(String(text || ''));
}

function wantsBank(text) {
  const t = String(text || '');
  if (/\bbank holiday\b/i.test(t)) return false;
  if (/\b(digital bank|online bank|neobank|mobile bank|banking app)\b/i.test(t)) return true;
  return /\b(bank|banking)\b/i.test(t) && /\b(create|build|make|design|generate|want|website|site|landing page|homepage)\b/i.test(t);
}

function wantsSearch(text) {
  const t = String(text || '');
  if (/\b(search engine|web search)\b/i.test(t)) return true;
  return /\bsearch\b/i.test(t) && /\b(engine|results|query|queries)\b/i.test(t);
}

function wantsJobs(text) {
  return /\b(job platform|jobs platform|job board|jobs board|job site|career site|hiring platform|recruiting platform|recruitment site)\b/i.test(String(text || ''));
}

const STORE_NAMES = ['amazon', 'ebay', 'etsy', 'walmart', 'shopify', 'target', 'aliexpress', 'alibaba', 'ikea'];

function wantsStore(text) {
  const t = String(text || '');
  if (STORE_NAMES.some((name) => new RegExp(`\\b${name}\\b`, 'i').test(t))) return true;
  return /\b(shop|store|marketplace|e-?commerce)\b/i.test(t) && /\b(website|site|page|like|create|build|make|design)\b/i.test(t);
}

const SEARCH_NAMES = new Set(['google', 'bing', 'duckduckgo', 'yahoo', 'baidu']);

export function isChatFollowUp(text) {
  const t = String(text || '').trim().toLowerCase().replace(/[!?.]+$/g, '').replace(/\s+/g, ' ');
  return [
    'wdym',
    'what do you mean',
    'what do u mean',
    'huh',
    'what',
    'why',
    'tell me what to use',
    'i could not look that up',
  ].includes(t);
}

export function explainPrevious(previous) {
  const prev = String(previous || '').replace(/\s+/g, ' ').trim();
  if (!prev) return 'Tell me which line to explain.';
  if (/building it on the computer/i.test(prev)) {
    return 'I meant the website is being written in the computer panel beside this chat. The files show up there, and this chat line stays short while that happens.';
  }
  if (/what is the site for|say what the site is for/i.test(prev)) {
    return 'I meant I will not guess a page. Tell me what the site is for, and I will build that.';
  }
  if (/what should a visitor do first|what should people do first/i.test(prev)) {
    return 'I meant the first thing someone should do on the page — search, shop, sign up, read, or whatever fits.';
  }
  if (/who is (?:the page|it) for/i.test(prev)) {
    return 'I meant who the page is written for — locals, customers, students, and so on.';
  }
  if (/could not look that up/i.test(prev)) {
    return 'I meant the public page for that name did not come back. Your next message is a question about that line, not the name of a website.';
  }
  return `I meant: ${prev}`;
}

function isSearchName(name) {
  return SEARCH_NAMES.has(String(name || '').trim().toLowerCase());
}

function titleName(name) {
  const raw = String(name || '').trim();
  if (!raw) return '';
  const known = BRAND_KEYS.get(raw.toLowerCase());
  if (known) return known;
  return titleCase(raw);
}

export function referenceQuery(text) {
  const raw = String(text || '');
  const match = raw.match(/\b(?:similar to|like)\s+(?:an?\s+)?(.+)$/i);
  if (!match) return '';
  const name = match[1]
    .replace(/\b(website|web site|site|landing page|homepage|page|please|for me)\b/gi, ' ')
    .replace(/[^a-z0-9\s.'+-]/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (name.length < 2) return '';
  if (/^(airbnb|booking\.com)$/i.test(name)) return '';
  return name;
}

function namedProductQuery(text) {
  const raw = String(text || '');
  if (referenceQuery(raw)) return '';
  const made = raw.match(/\b(?:create|build|make|design|generate)\s+(?:me\s+)?(?:an?\s+)?(.+?)\s+(?:website|web site|site|homepage|landing page)\b/i);
  if (!made) return '';
  const subject = made[1]
    .replace(/^(?:a|an|the|my|our)\s+/i, '')
    .replace(/^(?:website|web site|site|page|homepage)[.!]?\s*/i, '')
    .replace(/\b(with|that|which|including|includes?).*$/i, ' ')
    .replace(/[^a-z0-9\s'-]/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  const tokens = subject.split(' ').filter(Boolean);
  if (tokens.length > 5 || looksLikePastedBrief(subject)) return '';
  if (!subject || /^(a|an|the|my|our|me|website|site|page)$/i.test(subject) || isChatFollowUp(subject)) {
    return explicitBrand(raw) || '';
  }
  return subject;
}

export function skipsReferenceLookup(text) {
  return wantsBank(text) || wantsSearch(text) || wantsJobs(text) || wantsStays(text) || wantsAgent(text) || wantsBlog(text)
    || /\b(restaurant|cafe|cafÃ©|bakery|coffee shop|diner|bistro|shop|store|boutique|marketplace|school|course|class|lesson|tutor|university|clinic|doctor|dentist|hospital|pharmacy)\b/i.test(String(text || ''));
}

export function isWebsiteRequest(text) {
  if (isChatFollowUp(text)) return false;
  const t = String(text || '').toLowerCase();
  const make = /\b(create|build|make|design|generate|want)\b/.test(t);
  const site = /\b(website|web site|landing page|homepage|site)\b/.test(t);
  const likeStay = /\blike\s+(an?\s+)?(airbnb|booking\.com)\b/.test(t);
  const known = wantsBank(text) || wantsAgent(text) || wantsSearch(text) || wantsJobs(text);
  return (make && site) || likeStay || (make && known) || (site && known);
}

export function isDeckRequest(text) {
  return /\b(powerpoint|pptx|presentation|slide deck|slides)\b/i.test(String(text || ''));
}

function deckTopic(text) {
  const topic = String(text || '')
    .replace(/\b(please|can you|could you|i want|want|make|create|build|generate|design|a|an|the|powerpoint|pptx|presentation|slide deck|slides|about|for|on|me)\b/gi, ' ')
    .replace(/[^a-z0-9\s'-]/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return topic || '';
}

function capPhrase(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';
  return raw.charAt(0).toUpperCase() + raw.slice(1);
}

function zolaidLaunchSlides(topic) {
  const title = capPhrase(topic) || 'Launching Zolaid Cloud';
  const product = /\bcloud\b/i.test(title) ? 'Zolaid Cloud' : (topic ? 'Zolaid' : 'Zolaid Cloud');
  return [
    {
      layout: 'title',
      kicker: product,
      title,
      subtitle: 'The computer writes the work while the room is still in the meeting.',
      body: 'A launch briefing for the first teams who need a deck, a page, or a file the same afternoon.',
    },
    {
      layout: 'agenda',
      kicker: 'Agenda',
      title: 'Eight minutes, then a yes or a no',
      items: [
        'Why the first draft still takes the hour',
        `What ${product} does with one request`,
        'How the file and the slides stay the same',
        'Who we put this in front of first',
        'The offer, and how to start',
      ],
      body: 'Five decisions. Then we either start the launch or we do not.',
    },
    {
      layout: 'points',
      kicker: 'The wait',
      title: 'Three places the week disappears',
      points: [
        { title: 'The blank page', body: 'The meeting ends. Someone still has to invent the outline from scratch.' },
        { title: 'Two copies', body: 'Chat has one version. The file has another. Nobody knows which line is current.' },
        { title: 'Nothing to read', body: 'The room leaves with a promise to send something later.' },
      ],
      body: 'The delay is not the making. It is the empty start, the second copy, and the missing page.',
    },
    {
      layout: 'stat',
      kicker: 'The shift',
      stat: '1',
      statLabel: 'source of truth',
      title: 'The slide title is the title in the file',
      body: `${product} writes the deck and the code from the same sentences. The room sees the slide. The file still matches.`,
    },
    {
      layout: 'quote',
      kicker: 'The line we keep',
      title: 'Put a finished deck on the screen before anyone asks where the file went.',
      quote: 'Put a finished deck on the screen before anyone asks where the file went.',
      attribution: `How we talk about launching ${product}`,
      body: 'Put a finished deck on the screen before anyone asks where the file went.',
    },
    {
      layout: 'columns',
      kicker: 'Day one',
      title: 'What the presenter gets',
      columns: [
        { title: 'Before', body: 'A request sits in chat. The deck is rebuilt by hand after the call. Titles drift from the notes.' },
        { title: `With ${product}`, body: 'The computer opens at once. Files are written in view. When they finish, only the slides remain.' },
      ],
      body: 'Before, the deck is rebuilt later. With the product, the slides are what is left on screen.',
    },
    {
      layout: 'steps',
      kicker: 'How a request runs',
      title: 'From one sentence to a deck',
      steps: [
        { title: 'Open the computer', body: 'The screen becomes the work surface as soon as the request lands.' },
        { title: 'Write in the open', body: 'Thinking first, then system.json, then slides.js, one file across the screen.' },
        { title: 'Leave the slides up', body: 'The menus stay out of the way until the writing is finished.' },
      ],
      body: 'Open the computer, write the source in view, then leave the designed slides on screen.',
    },
    {
      layout: 'close',
      kicker: 'The ask',
      title: 'Ten teams. This week.',
      body: 'Start with people who already need a deck, a page, or a file the same afternoon.',
      cta: 'hello@zolaid.com',
    },
  ];
}

function topicPitchSlides(topic) {
  const title = capPhrase(topic);
  const subject = String(topic || '').trim();
  const lower = subject.charAt(0).toLowerCase() + subject.slice(1);
  return [
    {
      layout: 'title',
      kicker: 'Briefing',
      title,
      subtitle: `A short case for ${lower}, so this room can decide without booking another hour.`,
      body: `The decision in front of us is ${lower}.`,
    },
    {
      layout: 'agenda',
      kicker: 'Agenda',
      title: 'What we will settle',
      items: [
        `Why ${lower} is in the room today`,
        'The three facts that change the decision',
        'What is already working',
        'What is still in the way',
        'The ask before we leave',
      ],
      body: `We will leave with a decision on ${lower}, or we will name what is still missing.`,
    },
    {
      layout: 'points',
      kicker: 'The case',
      title: `Three facts about ${lower}`,
      points: [
        { title: 'It has a cost', body: `Leaving ${lower} vague keeps the same debate on next week's agenda.` },
        { title: 'It has an owner', body: 'One person leaves able to repeat the decision without these slides.' },
        { title: 'It has a next step', body: 'We name the move, the owner, and the week it happens.' },
      ],
      body: `${title} costs us time if it stays vague, and it needs one owner plus one dated next step.`,
    },
    {
      layout: 'stat',
      kicker: 'The window',
      stat: '90',
      statLabel: 'days',
      title: `${title} fits in one quarter`,
      body: 'Long enough to see if the decision was real. Short enough that nobody hides inside a roadmap.',
    },
    {
      layout: 'quote',
      kicker: 'Say it once',
      title: `${title} is a decision, not a status update.`,
      quote: `${title} is a decision, not a status update.`,
      attribution: 'The line this briefing is built to support',
      body: `${title} is a decision, not a status update.`,
    },
    {
      layout: 'columns',
      kicker: 'The contrast',
      title: `Where ${lower} stands`,
      columns: [
        { title: 'Holding', body: 'The work people can already point to. Keep it. Do not reopen it in this meeting.' },
        { title: 'Open', body: 'The part that still needs a name, an owner, and a date before anyone leaves.' },
      ],
      body: 'Keep what is already holding. Name the open part before the meeting ends.',
    },
    {
      layout: 'steps',
      kicker: 'The plan',
      title: 'How we run the next stretch',
      steps: [
        { title: 'Name the owner', body: `One person is accountable for ${lower} after today.` },
        { title: 'Cut the scope', body: 'Ship the smallest version that lets us learn, and park the rest.' },
        { title: 'Review on a date', body: 'Put the check-in on the calendar before the meeting ends.' },
      ],
      body: `Name an owner for ${lower}, cut the scope, and book the review before we stand up.`,
    },
    {
      layout: 'close',
      kicker: 'The ask',
      title: `Agree the next step on ${lower}`,
      body: 'Leave with an owner, a date, and one sentence the rest of the company can repeat.',
      cta: 'Decide before we close',
    },
  ];
}

function deckSlides(topic) {
  const raw = String(topic || '').trim();
  if (!raw || /\bzolaid\b/i.test(raw)) return zolaidLaunchSlides(raw);
  return topicPitchSlides(raw);
}

export function createDeckSource(text) {
  const topic = deckTopic(text);
  const slides = deckSlides(topic);
  return {
    kind: 'slides',
    topic: topic || 'Launching Zolaid Cloud',
    accent: '#0c3d34',
    ink: '#1c1917',
    paper: '#f6f1e8',
    gold: '#e7c99a',
    slides,
  };
}

function deckFiles(source) {
  const data = {
    topic: source.topic,
    accent: source.accent,
    paper: source.paper,
    slides: source.slides,
  };
  const slidesJs = `const deck = ${JSON.stringify(data, null, 2)};

const root = document.querySelector('#deck');

deck.slides.forEach((slide, index) => {
  const card = document.createElement('article');
  card.className = 'slide slide-' + (slide.layout || 'body');
  card.dataset.layout = slide.layout || 'body';

  const kicker = document.createElement('p');
  kicker.className = 'kicker';
  kicker.textContent = slide.kicker || '';

  const title = document.createElement('h1');
  title.textContent = slide.title || '';

  const body = document.createElement('p');
  body.className = 'body';
  body.textContent = slide.subtitle || slide.body || slide.quote || '';

  const page = document.createElement('span');
  page.className = 'page';
  page.textContent = String(index + 1).padStart(2, '0');

  card.append(kicker, title, body, page);
  if (root) root.append(card);
});
`;
  return [
    { path: 'system.json', language: 'json', content: JSON.stringify(source, null, 2) },
    { path: 'slides.js', language: 'javascript', content: slidesJs },
  ];
}

const STAYS = [
  { cat: 'Amazing views', title: 'Cliff house with a glass wall', place: 'Amalfi, Italy', price: 412, rating: '4.97', img: 'https://images.unsplash.com/photo-1613490493576-7fde63acd811?auto=format&fit=crop&w=900&q=80' },
  { cat: 'Design', title: 'Concrete loft over the canal', place: 'Amsterdam, Netherlands', price: 228, rating: '4.91', img: 'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?auto=format&fit=crop&w=900&q=80' },
  { cat: 'Beachfront', title: 'White cottage on the sand', place: 'Tulum, Mexico', price: 310, rating: '4.88', img: 'https://images.unsplash.com/photo-1499793983690-e29da59ef1c2?auto=format&fit=crop&w=900&q=80' },
  { cat: 'Cabins', title: 'Pine cabin with a wood stove', place: 'Lake Tahoe, United States', price: 189, rating: '4.95', img: 'https://images.unsplash.com/photo-1449158743715-0a90ebb6d2d8?auto=format&fit=crop&w=900&q=80' },
  { cat: 'Design', title: 'Sunlit apartment with oak floors', place: 'Lisbon, Portugal', price: 146, rating: '4.86', img: 'https://images.unsplash.com/photo-1505691938895-1758d7feb511?auto=format&fit=crop&w=900&q=80' },
  { cat: 'Amazing views', title: 'Hill villa and a long pool', place: 'Santorini, Greece', price: 540, rating: '4.99', img: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?auto=format&fit=crop&w=900&q=80' },
  { cat: 'City', title: 'Studio beside the morning market', place: 'Kyoto, Japan', price: 132, rating: '4.9', img: 'https://images.unsplash.com/photo-1554995207-c18c203602cb?auto=format&fit=crop&w=900&q=80' },
  { cat: 'Beachfront', title: 'Deck house facing the reef', place: 'Bali, Indonesia', price: 205, rating: '4.84', img: 'https://images.unsplash.com/photo-1540541338287-41700207dee6?auto=format&fit=crop&w=900&q=80' },
];

function wantsStays(text) {
  return /airbnb|booking\.com|vacation|homestay|rental home|\bstays?\b/i.test(String(text || ''));
}

function wantsBlog(text) {
  const t = String(text || '');
  if (/\b(blog|blogging|newsletter|journal|diary)\b/i.test(t)) return true;
  return /\bpersonal\b/i.test(t) && /\b(writing|writer|posts?|articles?|stories)\b/i.test(t);
}

function explicitBrand(text) {
  const raw = String(text || '');
  const quoted = raw.match(/\b(?:called|named|brand(?:ed)?)\s+["']([^"']{2,40})["']/i);
  if (quoted) return quoted[1].trim();
  const named = raw.match(/\b(?:called|named|brand(?:ed)?)\s+([A-Za-z][\w-]{1,24}(?:\s+[A-Za-z][\w-]{1,24}){0,2})/i);
  if (named) return named[1].trim();
  const forName = raw.match(/\bfor\s+([A-Z][A-Za-z0-9][\w-]{1,24}(?:\s+[A-Z][A-Za-z0-9][\w-]{0,24}){0,2})\b/);
  if (forName && !/^(The|This|Your|Our|New|Simple|Clean|Modern)$/i.test(forName[1])) return forName[1].trim();
  return '';
}
function pickBrand(text, fallback) {
  const named = explicitBrand(text);
  if (named) return named.toLowerCase();
  return fallback;
}

function bankBrand(text) {
  const named = explicitBrand(text);
  if (named) return named;
  const proper = String(text || '').match(/\b([A-Z][a-zA-Z0-9]{1,24})\s+(?:digital\s+|online\s+|mobile\s+)?bank\b/);
  if (proper && !/^(The|This|Create|Build|Make|Design|Please|Want|My|Our)$/.test(proper[1])) return proper[1];
  if (/\bdigital\s+bank\b/i.test(text)) return 'Digital Bank';
  if (/\bonline\s+bank\b/i.test(text)) return 'Online Bank';
  if (/\bmobile\s+bank\b/i.test(text)) return 'Mobile Bank';
  if (/\bneobank\b/i.test(text)) return 'Neobank';
  return 'Bank';
}

function staysSource(brief) {
  return {
    kind: 'stays',
    brand: pickBrand(brief, 'haven'),
    accent: '#ff385c',
    ink: '#222222',
    header: '#ffffff',
    nav: ['Stays', 'Experiences'],
    categories: ['All', 'Amazing views', 'Beachfront', 'Cabins', 'Design', 'City'],
    listings: STAYS,
  };
}

const COLORS = {
  red: '#e11d48',
  black: '#18181b',
  blue: '#2563eb',
  green: '#0f766e',
  pink: '#ff385c',
  teal: '#0f766e',
  white: '#f4f4f2',
  orange: '#ea580c',
  purple: '#7c3aed',
  yellow: '#ca8a04',
};

const THEME_FROM_COLOR = {
  red: { accent: '#e11d48', ink: '#1c1917', header: '#fff5f5' },
  black: { accent: '#18181b', ink: '#18181b', header: '#f4f4f5' },
  blue: { accent: '#2563eb', ink: '#172033', header: '#eff6ff' },
  green: { accent: '#0f766e', ink: '#132422', header: '#f0fdfa' },
  pink: { accent: '#ff385c', ink: '#1c1917', header: '#fff1f2' },
  teal: { accent: '#0f766e', ink: '#132422', header: '#f0fdfa' },
  white: { accent: '#52525b', ink: '#18181b', header: '#fafafa' },
  orange: { accent: '#ea580c', ink: '#1c1917', header: '#fff7ed' },
  purple: { accent: '#7c3aed', ink: '#1e1b4b', header: '#f5f3ff' },
  yellow: { accent: '#ca8a04', ink: '#1c1917', header: '#fefce8' },
};

const BRIEF_FILLER = /\b(please|can you|could you|i want|i need|want|need|make|create|build|generate|design|for me|me|a|an|the|website|web site|landing page|homepage|home page|site|page|pages|for|my|our|called|named|branded|brand|with|that|which|includes?|including|featuring|feature|features|section|sections|theme|color|colour|colors|colours|simple|clean|focusing|focused|focus|personal content|content|it|is|are|and|or|to|of|in|on|from|this|brief)\b/gi;

function looksLikePastedBrief(value) {
  const text = String(value || '').replace(/\s+/g, ' ').trim();
  if (!text) return false;
  const words = text.split(/\s+/).filter(Boolean);
  if (/\b(it includes|with a .+ theme|color theme|colour theme|focusing on|built from this brief|create a website)\b/i.test(text)) return true;
  if (words.length > 8) return true;
  if (words.length > 6 && /\b(includes?|section|theme|homepage|landing)\b/i.test(text)) return true;
  return false;
}

function clampWords(value, maxWords = 4, maxChars = 36) {
  const words = String(value || '').replace(/\s+/g, ' ').trim().split(/\s+/).filter(Boolean);
  let out = words.slice(0, maxWords).join(' ');
  if (out.length > maxChars) {
    out = out.slice(0, maxChars).replace(/\s+\S*$/, '').trim();
  }
  return out;
}

function briefSubject(text) {
  return String(text || '')
    .replace(BRIEF_FILLER, ' ')
    .replace(/[^a-z0-9\s'-]/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function titleCase(value) {
  return String(value || '').split(/\s+/).filter(Boolean).map((word) => {
    if (word.toLowerCase() === 'ai') return 'AI';
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  }).join(' ');
}

function categoryBrand(text) {
  const t = String(text || '');
  if (wantsBlog(t)) {
    if (/\bpersonal\s+blog\b/i.test(t)) return 'Personal Blog';
    if (/\bnewsletter\b/i.test(t)) return 'Newsletter';
    return 'Blog';
  }
  if (/\b(portfolio|resume|cv)\b/i.test(t)) return 'Portfolio';
  if (/\b(restaurant|cafe|caf[eé]|bakery|diner|bistro)\b/i.test(t)) return 'Kitchen';
  if (/\b(shop|store|boutique|marketplace)\b/i.test(t)) return 'Shop';
  if (/\b(school|course|class|lesson|tutor|university)\b/i.test(t)) return 'School';
  if (/\b(clinic|doctor|dentist|hospital|pharmacy)\b/i.test(t)) return 'Clinic';
  if (wantsAgent(t)) return 'Autonomous AI Agent';
  return '';
}

function shortBrandFromBrief(text, fallback = 'Studio') {
  const named = explicitBrand(text);
  if (named && !looksLikePastedBrief(named) && !/^(a|an|the)\s+simple\b/i.test(named) && !/^(simple|clean|modern)$/i.test(named)) {
    const kept = /[a-z][A-Z]|[A-Z]{2,}/.test(named) ? named : titleCase(named);
    return clampWords(kept, 3, 28);
  }
  const category = categoryBrand(text);
  if (category) return category;
  const subjectHint = siteSubject(text);
  if (
    subjectHint
    && !looksLikePastedBrief(subjectHint)
    && !/^(website|site|page|simple|clean|modern)$/i.test(subjectHint)
    && !/^(a|an|the)\s+simple\b/i.test(subjectHint)
  ) {
    return clampWords(subjectHint, 4, 32);
  }
  const subject = briefSubject(text);
  if (!subject) return fallback;
  const short = clampWords(titleCase(subject), 3, 28);
  if (!short || looksLikePastedBrief(short) || short.split(/\s+/).length > 4) return fallback;
  if (/^(a|an|the)\s+simple\b/i.test(short) || /^(simple|clean|modern|saas|product|website|landing)$/i.test(short) || /^(simple|clean|modern)\s+(modern|clean|simple|product|website|site)$/i.test(short)) return fallback;
  return short;
}
function labelFromBrief(text, subject) {
  const named = explicitBrand(text);
  if (named && !looksLikePastedBrief(named)) return clampWords(titleCase(named), 3, 28);
  if (subject && !looksLikePastedBrief(subject)) return clampWords(titleCase(subject), 4, 32);
  return shortBrandFromBrief(text, '');
}

function accentFromBrief(text, fallback = '#143d36') {
  const raw = String(text || '');
  const hex = raw.match(/#(?:[0-9a-f]{3}|[0-9a-f]{6})\b/i);
  if (hex) return hex[0];
  const named = raw.match(/\b(red|black|blue|green|pink|teal|white|orange|purple|yellow)\b/i);
  if (named) return COLORS[named[1].toLowerCase()] || fallback;
  return fallback;
}

function themeFromBrief(text, defaults = {}) {
  const raw = String(text || '');
  const named = raw.match(/\b(red|black|blue|green|pink|teal|white|orange|purple|yellow)\b/i);
  if (named) {
    const palette = THEME_FROM_COLOR[named[1].toLowerCase()];
    if (palette) {
      return {
        accent: palette.accent,
        ink: palette.ink,
        header: palette.header,
      };
    }
  }
  const accent = accentFromBrief(raw, defaults.accent || '#143d36');
  return {
    accent,
    ink: defaults.ink || '#14221f',
    header: defaults.header || '#f3f6f4',
  };
}

/** Office hallway stock photo formerly reused as the universal productHtml fallback. */
const HALLWAY_HERO = 'https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=1200&q=80';

const HERO_IMAGE_POOLS = {
  coffee: [
    'https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1501339847302-ac426a4a7cbb?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1442512595331-e89e7387617f?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1511920170033-f8396924c348?auto=format&fit=crop&w=1200&q=80',
  ],
  food: [
    'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1504674900247-0877df9cc836?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1559339352-11d035aa65de?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1466978913421-dad2ebd01d17?auto=format&fit=crop&w=1200&q=80',
  ],
  blog: [
    'https://images.unsplash.com/photo-1455390582262-044cdead277a?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1516414447565-b14be3849460?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1499750310107-5fef28a66643?auto=format&fit=crop&w=1200&q=80',
  ],
  saas: [
    'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1519389950473-47ba0277781c?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1556761175-b413da4baf72?auto=format&fit=crop&w=1200&q=80',
  ],
  shop: [
    'https://images.unsplash.com/photo-1441986300917-64674bd600d8?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1472851294608-062f824d29cc?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1523384920190-7ef64930aeb9?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1483985988355-763728e1935b?auto=format&fit=crop&w=1200&q=80',
  ],
  school: [
    'https://images.unsplash.com/photo-1503676260728-1c00da094a0b?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1523050854058-8df90110c9f1?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1497633762265-9d179a990aa6?auto=format&fit=crop&w=1200&q=80',
  ],
  clinic: [
    'https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1631217868264-e5b90bb7e133?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1584982751601-97dcc096659c?auto=format&fit=crop&w=1200&q=80',
  ],
  portfolio: [
    'https://images.unsplash.com/photo-1498050108023-c5249f4df085?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1507238691740-187a5b1d37b8?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1542744173-8e2bd585f2ad?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?auto=format&fit=crop&w=1200&q=80',
  ],
  fitness: [
    'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1517836357463-d25dfeac3438?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1518611012118-696072aa579a?auto=format&fit=crop&w=1200&q=80',
  ],
  travel: [
    'https://images.unsplash.com/photo-1488646953014-85cb44e25828?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?auto=format&fit=crop&w=1200&q=80',
  ],
  finance: [
    'https://images.unsplash.com/photo-1563986768609-322da13575f3?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1553729459-efe14ef6055d?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?auto=format&fit=crop&w=1200&q=80',
  ],
  agent: [
    'https://images.unsplash.com/photo-1677442136019-21780ecad995?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1620712943543-bcc4688e7485?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1485827404703-89b55fcc595e?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1531746790731-6c087fecd65a?auto=format&fit=crop&w=1200&q=80',
  ],
  red: [
    'https://images.unsplash.com/photo-1516035069371-29a1b244cc32?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1493612276216-ee3925520721?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1550684848-fac1c5b4e853?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1518709268805-4e9042af9f23?auto=format&fit=crop&w=1200&q=80',
  ],
  general: [
    'https://images.unsplash.com/photo-1497366811353-6870744d04b2?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1497215728101-856f4ea42174?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1557804506-669a67965ba0?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1504384308090-c894fdcc538d?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=1200&q=80',
  ],
};

function briefHash(text) {
  const s = String(text || '');
  let h = 2166136261;
  for (let i = 0; i < s.length; i += 1) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function heroCategoryFromBrief(brief) {
  const raw = String(brief || '');
  if (/\b(coffee|cafe|caf[eé]|espresso|latte|barista)\b/i.test(raw)) return 'coffee';
  if (/\b(restaurant|bakery|diner|bistro|kitchen|food|menu|pizza|sushi)\b/i.test(raw)) return 'food';
  if (wantsBlog(raw) || /\b(writer|writing|newsletter|journal)\b/i.test(raw)) return 'blog';
  if (wantsAgent(raw) || /\b(ai|agent|robot|automation)\b/i.test(raw)) return 'agent';
  if (/\b(saas|software|dashboard|analytics|startup|product|app|platform)\b/i.test(raw)) return 'saas';
  if (/\b(shop|store|boutique|marketplace|ecommerce|e-commerce|retail)\b/i.test(raw)) return 'shop';
  if (/\b(school|course|class|lesson|tutor|university|education|learn)\b/i.test(raw)) return 'school';
  if (/\b(clinic|doctor|dentist|hospital|pharmacy|health|medical)\b/i.test(raw)) return 'clinic';
  if (/\b(portfolio|resume|cv|designer|creative|studio)\b/i.test(raw)) return 'portfolio';
  if (/\b(gym|fitness|workout|yoga|sport|training)\b/i.test(raw)) return 'fitness';
  if (/\b(travel|trip|tour|hotel|vacation|flight)\b/i.test(raw)) return 'travel';
  if (wantsBank(raw) || /\b(finance|fintech|banking|invest|money|wallet)\b/i.test(raw)) return 'finance';
  if (/\bred\b/i.test(raw) && !/\bread\b/i.test(raw)) return 'red';
  return 'general';
}

function pickFromPool(pool, seed) {
  const list = Array.isArray(pool) && pool.length ? pool : HERO_IMAGE_POOLS.general;
  return list[seed % list.length];
}

/** Distinct Unsplash hero per brief: category keywords + hash seed so two briefs rarely match. */
function heroImageFromBrief(brief) {
  const raw = String(brief || '').trim() || 'studio';
  const category = heroCategoryFromBrief(raw);
  const pool = HERO_IMAGE_POOLS[category] || HERO_IMAGE_POOLS.general;
  const seed = briefHash(`${category}:${raw.toLowerCase()}`);
  const picked = pickFromPool(pool, seed);
  if (picked === HALLWAY_HERO) {
    return pickFromPool(HERO_IMAGE_POOLS.general, seed + 17);
  }
  return picked;
}

function sectionId(label) {
  return String(label || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    || 'section';
}

function topicPhrase(brief, brand) {
  const subject = briefSubject(brief);
  if (subject && !looksLikePastedBrief(subject) && subject.split(/\s+/).length <= 6) {
    return subject.toLowerCase();
  }
  const category = heroCategoryFromBrief(brief);
  if (category === 'coffee') return 'coffee and the morning crowd';
  if (category === 'food') return 'food worth booking';
  if (category === 'blog') return 'writing and notes';
  if (category === 'saas') return 'software teams ship faster';
  if (category === 'shop') return 'goods you can order today';
  if (category === 'school') return 'lessons that stick';
  if (category === 'clinic') return 'care you can book';
  if (category === 'portfolio') return 'selected work';
  if (category === 'fitness') return 'training that fits the week';
  if (category === 'travel') return 'trips planned with less friction';
  if (category === 'finance') return 'money that stays clear';
  if (category === 'agent') return 'agents that finish the task';
  return String(brand || 'this product').toLowerCase();
}

function audienceHint(brief) {
  const raw = String(brief || '');
  const match = raw.match(/\bfor\s+((?:[a-z][\w'-]*)(?:\s+[a-z][\w'-]*){0,3})/i);
  if (match && !/^(a|an|the|my|our|me|website|site|page)\b/i.test(match[1])) {
    return match[1].replace(/\s+/g, ' ').trim();
  }
  const cat = heroCategoryFromBrief(raw);
  if (cat === 'coffee' || cat === 'food') return 'locals and walk-ins';
  if (cat === 'blog') return 'readers who want the next post';
  if (cat === 'saas') return 'founders and product teams';
  if (cat === 'shop') return 'customers ready to order';
  if (cat === 'school') return 'students starting this week';
  if (cat === 'clinic') return 'patients booking a visit';
  if (cat === 'portfolio') return 'clients reviewing the work';
  if (cat === 'fitness') return 'members who train on a schedule';
  if (cat === 'travel') return 'travelers planning the next trip';
  if (cat === 'finance') return 'people who want a clear balance';
  if (cat === 'agent') return 'operators handing off real work';
  return 'people who need a clear next step';
}

function heroCopyFromBrief(brief, brand) {
  const raw = String(brief || '').replace(/\s+/g, ' ').trim();
  const topic = topicPhrase(raw, brand);
  const who = audienceHint(raw);
  const cat = heroCategoryFromBrief(raw);
  if (wantsBlog(raw) || cat === 'blog') {
    return {
      title: 'Notes worth opening.',
      lead: brand + ' publishes stories and updates for ' + who + '. Start with the latest post, then browse what came before.',
      cta: 'Read the latest',
    };
  }
  if (cat === 'coffee') {
    return {
      title: 'Coffee, ready when you are.',
      lead: brand + ' is built around ' + topic + '. See what is pouring, grab a seat, or order ahead for ' + who + '.',
      cta: 'See the menu',
    };
  }
  if (cat === 'food') {
    return {
      title: 'A table worth keeping.',
      lead: brand + ' cooks for ' + who + '. Check tonight\'s dishes, open sittings, and book in one pass.',
      cta: 'Book a table',
    };
  }
  if (cat === 'saas') {
    return {
      title: 'Ship the work that matters.',
      lead: brand + ' helps ' + who + ' with ' + topic + '. One workspace for the plan, the status, and the handoff.',
      cta: 'Start free',
    };
  }
  if (cat === 'shop') {
    return {
      title: 'Goods you can take home.',
      lead: brand + ' lists what is in stock for ' + who + '. Price, availability, and checkout stay on this page.',
      cta: 'Browse stock',
    };
  }
  if (cat === 'school') {
    return {
      title: 'Lessons with a clear hour.',
      lead: brand + ' teaches ' + topic + ' for ' + who + '. Pick a class, see who leads it, and enrol.',
      cta: 'Enrol now',
    };
  }
  if (cat === 'clinic') {
    return {
      title: 'Care you can schedule.',
      lead: brand + ' shows what it treats and which hours are open for ' + who + '. Request a visit without calling around.',
      cta: 'Request a visit',
    };
  }
  if (/\b(portfolio|resume|cv)\b/i.test(raw) || cat === 'portfolio') {
    return {
      title: 'Work worth reading.',
      lead: brand + ' shows selected projects for ' + who + ', with a short path to get in touch.',
      cta: 'See the work',
    };
  }
  if (cat === 'fitness') {
    return {
      title: 'Training that fits the week.',
      lead: brand + ' runs sessions for ' + who + '. See the schedule, the coaches, and claim a spot.',
      cta: 'View schedule',
    };
  }
  if (cat === 'travel') {
    return {
      title: 'Trips planned without the noise.',
      lead: brand + ' helps ' + who + ' lock dates, stays, and the first day on the ground.',
      cta: 'Plan a trip',
    };
  }
  if (cat === 'finance' || wantsBank(raw)) {
    return {
      title: 'Money with the fee on screen.',
      lead: brand + ' keeps balances clear for ' + who + '. Send a payment only after the cost is visible.',
      cta: 'Open an account',
    };
  }
  if (cat === 'agent' || wantsAgent(raw)) {
    return {
      title: 'An agent that finishes the task.',
      lead: brand + ' takes a goal from ' + who + ', plans the steps, and stops when it needs a decision.',
      cta: 'Start the agent',
    };
  }
  return {
    title: brand + ' for ' + clampWords(who, 4, 28) + '.',
    lead: brand + ' is built around ' + topic + '. ' + titleCase(who) + ' get a clear path from the first screen to the first result.',
    cta: 'Get started',
  };
}


function landingNavFor(cat, includePricing, includePosts) {
  if (cat === 'blog' || includePosts) return ['Home', 'Posts', 'About', 'Contact'];
  if (cat === 'coffee' || cat === 'food') return ['Menu', 'Visit', 'About', 'Book'];
  if (cat === 'shop') return ['Goods', 'How it works', 'About', 'Order'];
  if (cat === 'school') return ['Lessons', 'How it works', 'Pricing', 'Enrol'];
  if (cat === 'clinic') return ['Care', 'Visits', 'About', 'Book'];
  if (cat === 'fitness') return ['Programs', 'Schedule', 'Pricing', 'Join'];
  if (cat === 'portfolio') return ['Work', 'About', 'Process', 'Contact'];
  if (cat === 'travel') return ['Trips', 'How it works', 'About', 'Plan'];
  if (cat === 'finance') return ['Accounts', 'How it works', 'Fees', 'Open'];
  if (cat === 'agent') return ['Agent', 'Tasks', 'Safety', 'Plans'];
  if (includePricing || cat === 'saas') return ['Product', 'How it works', 'Pricing', 'About'];
  return ['Product', 'How it works', 'Proof', 'About'];
}

function featuresFromBrief(brief, brand, cat) {
  const topic = topicPhrase(brief, brand);
  const who = audienceHint(brief);
  if (cat === 'coffee') {
    return [
      { id: 'menu', title: 'What is pouring', body: brand + ' lists today\'s drinks and food so ' + who + ' know what is ready before they arrive.' },
      { id: 'space', title: 'Room to sit', body: 'Counter seats for a quick stop, tables for longer stays, and a corner when you need the laptop out.' },
      { id: 'order', title: 'Order ahead', body: 'Skip the line when you are in a rush. Name on the cup, ready at the time you pick.' },
      { id: 'beans', title: 'Beans and brew', body: 'Roast notes and brew methods stay on the page so regulars can ask for the same cup next time.' },
      { id: 'hours', title: 'Hours that hold', body: 'Open times and last call are published here. If a day changes, it changes on this page first.' },
    ];
  }
  if (cat === 'food') {
    return [
      { id: 'menu', title: 'Tonight\'s menu', body: 'Dishes, prices, and what has sold out. ' + brand + ' cooks from that list, not a hidden board.' },
      { id: 'tables', title: 'Open sittings', body: 'Lunch and dinner sit separately. A table for two is the default; larger parties leave a note.' },
      { id: 'book', title: 'Book in one step', body: 'Name, time, and party size. The kitchen holds the table and writes back if that hour is full.' },
      { id: 'kitchen', title: 'How we cook', body: topic + ' stays the focus — seasonal plates, clear allergens, and a kitchen you can ask about.' },
      { id: 'visit', title: 'Find us', body: 'Address, transit notes, and what to expect when you walk in.' },
    ];
  }
  if (cat === 'blog') {
    return [
      { id: 'home', title: 'Home', body: brand + ' opens on the latest writing and a short line about who is behind the page.' },
      { id: 'posts', title: 'Posts', body: 'Each post has a title, date, and opening paragraph you can finish in one sitting.' },
      { id: 'about', title: 'About', body: 'A short bio for ' + who + ', what this blog covers, and how to reach the writer.' },
      { id: 'topics', title: 'Topics', body: 'Notes on ' + topic + ', drafts in progress, and series you can follow without a paywall on day one.' },
    ];
  }
  if (cat === 'saas') {
    return [
      { id: 'workspace', title: 'One workspace', body: brand + ' keeps plans, status, and handoffs in one place so ' + who + ' stop bouncing between tools.' },
      { id: 'flow', title: 'From brief to done', body: 'Capture ' + topic + ', assign owners, and see what is blocked without a status meeting.' },
      { id: 'share', title: 'Share with context', body: 'Send a customer or teammate a live view — not a screenshot that is already stale.' },
      { id: 'automate', title: 'Light automation', body: 'Reminders and checklists run in the background. You stay in control of what goes out.' },
      { id: 'secure', title: 'Sensible defaults', body: 'Roles, invites, and audit trails ship on day one. No purple AI-magic layer required.' },
      { id: 'start', title: 'Start without a call', body: 'Create a workspace, invite two people, and ship the first milestone the same afternoon.' },
    ];
  }
  if (cat === 'shop') {
    return [
      { id: 'goods', title: 'In stock now', body: 'The list is what ' + brand + ' can sell today. Sold-out items stay visible with the return date.' },
      { id: 'orders', title: 'Orders by email', body: 'Pick an item, confirm the price, and send the order. The shop replies when it is packed.' },
      { id: 'shipping', title: 'Leaving today', body: 'Local pickup and courier options sit next to each item so there are no surprise delays.' },
      { id: 'care', title: 'Aftercare', body: 'Sizing notes, care instructions, and returns live on the product line — not buried in a FAQ maze.' },
    ];
  }
  if (cat === 'school') {
    return [
      { id: 'lessons', title: 'Upcoming lessons', body: 'Each class lists time, teacher, and the work you leave with. ' + brand + ' publishes that before you enrol.' },
      { id: 'path', title: 'A clear path', body: 'Start from zero or pick up ' + topic + ' again after a gap. Levels are named in plain language.' },
      { id: 'teachers', title: 'Teachers', body: 'Who leads the hour, what they have taught before, and how to ask a question before class.' },
      { id: 'enrol', title: 'Enrol', body: 'Leave a name and the lesson you want. The seat is held when the teacher confirms.' },
    ];
  }
  if (cat === 'clinic') {
    return [
      { id: 'care', title: 'What we treat', body: brand + ' lists the problems it handles and the ones it refers out. Read that before you book.' },
      { id: 'visits', title: 'Open hours', body: 'A visit is a set time with a named clinician. The next open slots stay on this page.' },
      { id: 'team', title: 'The team', body: 'Credentials, languages, and who you will see for a first visit.' },
      { id: 'book', title: 'Request a visit', body: 'Name, reason, and a phone number. The clinic confirms the time — no phone tree.' },
    ];
  }
  if (cat === 'portfolio') {
    return [
      { id: 'work', title: 'Selected work', body: 'Projects chosen to show how ' + brand + ' thinks about ' + topic + ', not a dump of every file.' },
      { id: 'process', title: 'Process', body: 'Discovery, a short proposal, build, and a handoff you can actually maintain.' },
      { id: 'about', title: 'About', body: 'Who is behind ' + brand + ', who they work with, and what a good fit looks like.' },
      { id: 'contact', title: 'Contact', body: 'A single path for ' + who + ' to start a conversation with a clear brief.' },
    ];
  }
  if (cat === 'fitness') {
    return [
      { id: 'programs', title: 'Programs', body: 'Strength, conditioning, and recovery tracks for ' + who + ' — named by the outcome, not the marketing jargon.' },
      { id: 'schedule', title: 'Schedule', body: 'This week\'s sessions with coaches and capacity. Claim a spot before it fills.' },
      { id: 'coaches', title: 'Coaches', body: 'Who leads the room, how they cue, and what beginners should expect on day one.' },
      { id: 'join', title: 'Join', body: brand + ' starts with a trial week. Cancel before it renews if it is not a fit.' },
    ];
  }
  if (cat === 'agent') {
    return [
      { id: 'agent', title: 'The agent', body: 'It works from a goal, not a single reply. It keeps going until the task is done or it needs you.' },
      { id: 'tasks', title: 'Tasks', body: 'Research, drafting, file work, and follow-ups. Each step stays visible on the page.' },
      { id: 'safety', title: 'Safety', body: 'You choose what it may touch. It asks before sending, paying, or changing something sensitive.' },
      { id: 'plans', title: 'Plans', body: 'Start with a trial. Pay for the runs it completes, and stop a run at any step.' },
    ];
  }
  return [
    { id: 'product', title: 'What you get', body: brand + ' focuses on ' + topic + ' for ' + who + '. The first screen shows the outcome, not a feature dump.' },
    { id: 'workflow', title: 'How it fits the day', body: 'Bring the brief in, see progress, and leave with a decision — built around how ' + who + ' already work.' },
    { id: 'proof', title: 'Why teams switch', body: 'Fewer tools, clearer owners, and a page you can send to someone who was not in the meeting.' },
    { id: 'setup', title: 'Setup in minutes', body: 'No multi-week onboarding theatre. Configure the essentials and invite the people who need access.' },
    { id: 'support', title: 'Human help', body: 'When something breaks, you reach a person who can see the same workspace you do.' },
  ];
}


function proofFromBrief(brief, brand, cat) {
  const seed = briefHash(brand + ':' + cat + ':proof');
  if (cat === 'coffee' || cat === 'food') {
    return [
      { value: (4 + (seed % 3)) + '.' + (8 + (seed % 2)) + '\u2605', label: 'Average guest rating' },
      { value: String(20 + (seed % 40)), label: 'Covers booked this week' },
      { value: String(6 + (seed % 6)), label: 'Years in the neighbourhood' },
    ];
  }
  if (cat === 'blog') {
    return [
      { value: String(12 + (seed % 40)), label: 'Posts published' },
      { value: (1 + (seed % 8)) + 'k', label: 'Monthly readers' },
      { value: 'Weekly', label: 'New notes' },
    ];
  }
  if (cat === 'shop') {
    return [
      { value: String(40 + (seed % 80)), label: 'Items in stock' },
      { value: (1 + (seed % 3)) + '-day', label: 'Typical dispatch' },
      { value: (96 + (seed % 4)) + '%', label: 'Orders delivered clean' },
    ];
  }
  if (cat === 'school' || cat === 'fitness') {
    return [
      { value: String(8 + (seed % 20)), label: 'Sessions this week' },
      { value: (90 + (seed % 9)) + '%', label: 'Seats filled' },
      { value: String(3 + (seed % 5)), label: 'Coaches on rotation' },
    ];
  }
  if (cat === 'clinic') {
    return [
      { value: (2 + (seed % 4)) + 'd', label: 'Typical wait for a visit' },
      { value: String(4 + (seed % 6)), label: 'Clinicians on rota' },
      { value: 'Same-day', label: 'Urgent callbacks' },
    ];
  }
  return [
    { value: (120 + (seed % 400)) + '+', label: 'Teams onboarded' },
    { value: (30 + (seed % 50)) + '%', label: 'Less status chasing' },
    { value: (2 + (seed % 4)) + 'w', label: 'To first shipped win' },
  ];
}

function testimonialsFromBrief(brief, brand, cat) {
  const who = audienceHint(brief);
  const topic = topicPhrase(brief, brand);
  if (cat === 'coffee' || cat === 'food') {
    return [
      { quote: 'We stop here before work. The page showed today\'s pour and we did not wait around guessing.', name: 'Maya R.', role: 'Regular' },
      { quote: brand + ' made booking a table feel like texting a friend — not filling a form from 2009.', name: 'Jon K.', role: 'Guest' },
    ];
  }
  if (cat === 'blog') {
    return [
      { quote: 'I open ' + brand + ' when I want one solid note, not a feed fighting for my attention.', name: 'Elena P.', role: 'Reader' },
      { quote: 'The posts read like someone who ships work, not someone performing content.', name: 'Chris T.', role: 'Subscriber' },
    ];
  }
  return [
    { quote: brand + ' cut the noise around ' + topic + '. We finally have one place ' + who + ' actually check.', name: 'Sam O.', role: 'Operator' },
    { quote: 'Setup took an afternoon. The first win showed up the same week — without a sales call.', name: 'Priya N.', role: 'Founder' },
  ];
}

function stepsFromBrief(brief, brand, cat) {
  if (cat === 'coffee' || cat === 'food') {
    return [
      { title: 'Check what is on', body: 'Open the menu or today\'s list before you leave the house.' },
      { title: 'Pick a time', body: 'Grab a walk-in window or book a table while seats remain.' },
      { title: 'Show up ready', body: brand + ' has your name, party size, and order notes waiting.' },
    ];
  }
  if (cat === 'blog') {
    return [
      { title: 'Read the latest', body: 'Start with the newest note on the home page.' },
      { title: 'Browse the archive', body: 'Jump by topic when you want the longer thread.' },
      { title: 'Say hello', body: 'Use the contact line when a post should become a conversation.' },
    ];
  }
  if (cat === 'shop') {
    return [
      { title: 'Browse stock', body: 'Only what can leave today stays marked available.' },
      { title: 'Place the order', body: 'Confirm price and delivery in one short form.' },
      { title: 'Get the update', body: brand + ' emails when it is packed or ready for pickup.' },
    ];
  }
  if (cat === 'clinic' || cat === 'school' || cat === 'fitness') {
    return [
      { title: 'Choose the offering', body: 'Lesson, visit, or session — named in plain language.' },
      { title: 'Request the hour', body: 'Leave your details once. No phone tree.' },
      { title: 'Get confirmation', body: brand + ' writes back with the time and who you will meet.' },
    ];
  }
  return [
    { title: 'Create a workspace', body: 'Invite the people who own ' + topicPhrase(brief, brand) + '.' },
    { title: 'Set the first goal', body: 'One milestone, clear owners, visible blockers.' },
    { title: 'Ship and share', body: 'Send ' + audienceHint(brief) + ' a live link instead of another status deck.' },
  ];
}

function aboutFromBrief(brief, brand, cat) {
  const topic = topicPhrase(brief, brand);
  const who = audienceHint(brief);
  if (cat === 'coffee') {
    return {
      title: 'Why ' + brand + ' exists',
      body: brand + ' is a neighbourhood coffee spot for ' + who + '. The site stays honest about what is pouring, when the room is quiet, and how to order without the usual cafe-site fluff.',
    };
  }
  if (cat === 'blog') {
    return {
      title: 'About ' + brand,
      body: brand + ' is a personal publish space for notes on ' + topic + '. It is written for ' + who + ' who want substance over a never-ending scroll.',
    };
  }
  if (cat === 'saas') {
    return {
      title: 'Built for ' + who,
      body: brand + ' exists because teams drowning in ' + topic + ' need a calmer system of record — not another purple dashboard that promises AI and ships vibes.',
    };
  }
  return {
    title: 'About ' + brand,
    body: brand + ' is built around ' + topic + ' for ' + who + '. This page is the product story in one place: what it does, how it works, and how to start.',
  };
}

function pricingFromBrief(brief, brand, cat) {
  if (cat === 'blog' || cat === 'portfolio' || cat === 'coffee' || cat === 'food') return null;
  if (cat === 'shop') {
    return [
      { name: 'Pickup', price: 'Free', note: 'Ready when the shelf says so.', cta: 'Browse goods' },
      { name: 'Courier', price: 'From \u00a34', note: 'Same-day windows where available.', cta: 'See delivery' },
      { name: 'Trade', price: 'Custom', note: 'Wholesale notes on request.', cta: 'Ask about trade' },
    ];
  }
  if (cat === 'school' || cat === 'fitness') {
    return [
      { name: 'Drop-in', price: '\u00a318', note: 'One session, no commitment.', cta: 'Book a drop-in' },
      { name: 'Monthly', price: '\u00a379', note: 'Unlimited scheduled classes.', cta: 'Start monthly' },
      { name: 'Private', price: '\u00a360', note: 'One-to-one hour with a coach.', cta: 'Request private' },
    ];
  }
  if (cat === 'clinic') {
    return [
      { name: 'Consult', price: '\u00a345', note: 'First visit, up to 30 minutes.', cta: 'Request consult' },
      { name: 'Follow-up', price: '\u00a335', note: 'Shorter revisit with notes carried over.', cta: 'Book follow-up' },
      { name: 'Care plan', price: 'Custom', note: 'Ongoing visits with a named clinician.', cta: 'Ask about plans' },
    ];
  }
  if (cat === 'agent') {
    return [
      { name: 'Trial', price: 'Free', note: 'Limited runs to prove the workflow.', cta: 'Start trial' },
      { name: 'Team', price: '\u00a349', note: 'Shared agents, audit log, priority queue.', cta: 'Choose team' },
      { name: 'Ops', price: 'Custom', note: 'Higher limits and dedicated review.', cta: 'Talk to us' },
    ];
  }
  return [
    { name: 'Starter', price: 'Free', note: 'Enough to prove ' + brand + ' on a real project.', cta: 'Start free' },
    { name: 'Growth', price: '\u00a329', note: 'More seats, shared views, and history that sticks.', cta: 'Choose growth' },
    { name: 'Business', price: '\u00a399', note: 'Roles, SSO-ready path, and a named human for escalations.', cta: 'Talk to sales' },
  ];
}

function postsFromBrief(brief, brand) {
  const topic = topicPhrase(brief, brand);
  return [
    { title: 'Why ' + brand + ' started writing about ' + topic, date: 'This week', excerpt: 'A short origin note for readers who want the point without a memoir.' },
    { title: 'A field guide to ' + topic, date: 'Last week', excerpt: 'Practical steps you can try this week, written for people who already have a day job.' },
    { title: 'What I am still figuring out', date: 'Earlier', excerpt: 'Open questions, drafts, and the next experiment on the desk.' },
  ];
}

function buildRichProduct(brief, opts) {
  opts = opts || {};
  const raw = String(brief || '').trim();
  const cat = opts.category || heroCategoryFromBrief(raw);
  let brand = opts.brand || shortBrandFromBrief(raw, opts.defaultBrand || 'Studio');
  if (/^(a|an|the)\s+simple\b/i.test(brand) || /^(simple|clean|modern|studio site|saas product|simple modern)$/i.test(brand)) {
    brand = opts.defaultBrand || categoryBrand(raw) || 'North';
  }
  const theme = themeFromBrief(raw, {
    accent: opts.accent || '#143d36',
    ink: opts.ink || '#14221f',
    header: opts.header || '#f3f6f4',
  });
  const includePosts = cat === 'blog' || wantsBlog(raw);
  const pricing = opts.pricing !== undefined ? opts.pricing : pricingFromBrief(raw, brand, cat);
  const includePricing = Array.isArray(pricing) && pricing.length > 0;
  const hero = opts.hero || heroCopyFromBrief(raw, brand);
  const features = opts.features || featuresFromBrief(raw, brand, cat);
  const nav = opts.nav || landingNavFor(cat, includePricing, includePosts);
  const imageBrief = raw + ' ' + brand + ' ' + cat;
  return sanitizeSourceCopy({
    kind: 'product',
    offer: opts.offer || cat || 'default',
    brand: brand,
    accent: theme.accent,
    ink: theme.ink,
    header: theme.header,
    image: opts.image || heroImageFromBrief(imageBrief),
    nav: nav,
    hero: hero,
    features: features,
    proof: opts.proof || proofFromBrief(raw, brand, cat),
    testimonials: opts.testimonials || testimonialsFromBrief(raw, brand, cat),
    steps: opts.steps || stepsFromBrief(raw, brand, cat),
    about: opts.about || aboutFromBrief(raw, brand, cat),
    pricing: includePricing ? pricing : undefined,
    posts: includePosts ? (opts.posts || postsFromBrief(raw, brand)) : undefined,
    footer: opts.footer || {
      note: brand + ' — built from your brief, ready to launch as an MVP landing.',
      links: nav.slice(0, 5),
    },
  });
}

function sanitizeSourceCopy(source) {
  if (!source || typeof source !== 'object') return source;
  const next = source;
  const brandText = String(next.brand || '');
  if (
    looksLikePastedBrief(brandText)
    || /\bwebsite\b/i.test(brandText)
    || /[.!?]/.test(brandText)
  ) {
    next.brand = shortBrandFromBrief(
      `${brandText} ${next.offer || ''} ${next.hero?.lead || ''}`,
      categoryBrand(`${brandText} ${next.offer || ''}`) || 'Studio',
    );
  } else if (next.brand) {
    next.brand = clampWords(brandText, 4, 36);
  }
  if (next.hero) {
    if (looksLikePastedBrief(next.hero.title) || /\bbuilt from this brief\b/i.test(String(next.hero.title || ''))) {
      const copy = heroCopyFromBrief(`${next.brand} ${next.offer || ''}`, next.brand || 'Studio');
      next.hero.title = copy.title;
    } else if (next.hero.title) {
      next.hero.title = clampWords(String(next.hero.title).replace(/\s+/g, ' ').trim(), 10, 72);
    }
    if (looksLikePastedBrief(next.hero.lead) || /\bbuilt from this brief\b/i.test(String(next.hero.lead || ''))) {
      const copy = heroCopyFromBrief(`${next.brand} ${next.offer || ''}`, next.brand || 'Studio');
      next.hero.lead = copy.lead;
    } else if (next.hero.lead) {
      const lead = String(next.hero.lead).replace(/\s+/g, ' ').trim();
      next.hero.lead = lead.length > 160 ? `${lead.slice(0, 157).replace(/\s+\S*$/, '')}…` : lead;
    }
  }
  if (!next.image || next.image === HALLWAY_HERO) {
    next.image = heroImageFromBrief([next.brand, next.offer, next.hero && next.hero.lead, next.hero && next.hero.title].filter(Boolean).join(' '));
  }
  return next;
}

function agentSource(brief) {
  return buildRichProduct(brief, {
    category: 'agent',
    offer: 'agent',
    defaultBrand: 'Autonomous AI Agent',
    accent: '#312e81',
    ink: '#1e1b4b',
    header: '#f6f5fb',
    nav: ['Agent', 'Tasks', 'Safety', 'Plans'],
  });
}
function namedPage(brief, page) {
  const brand = shortBrandFromBrief(brief, page.defaultBrand || 'Studio');
  const theme = themeFromBrief(brief, {
    accent: page.accent || '#143d36',
    ink: page.ink || '#14221f',
    header: page.header || '#f3f6f4',
  });
  const hero = typeof page.hero === 'function' ? page.hero(brand) : page.hero;
  const features = typeof page.features === 'function' ? page.features(brand) : page.features;
  const cat = page.category || heroCategoryFromBrief(brief) || page.offer || 'general';
  return buildRichProduct(brief, {
    category: cat,
    offer: page.offer || 'named',
    brand,
    accent: theme.accent,
    ink: theme.ink,
    header: theme.header,
    nav: page.nav,
    hero,
    features,
    image: page.image || heroImageFromBrief(`${brief} ${brand} ${cat}`),
    pricing: page.pricing,
    posts: page.posts,
    proof: page.proof,
    testimonials: page.testimonials,
    steps: page.steps,
    about: page.about,
    footer: page.footer,
  });
}
function blogSource(brief) {
  return buildRichProduct(brief, {
    category: 'blog',
    offer: 'blog',
    defaultBrand: 'Personal Blog',
    accent: '#e11d48',
    ink: '#1c1917',
    header: '#fff5f5',
  });
}

function defaultProductSource(brief) {
  if (wantsBlog(brief)) return blogSource(brief);
  return buildRichProduct(brief, {
    category: heroCategoryFromBrief(brief),
    offer: 'default',
    defaultBrand: 'Studio',
  });
}
function offeringSource(brief) {
  const raw = String(brief || '');
  if (wantsBlog(raw)) return blogSource(raw);
  if (/\b(coffee|cafe|caf[eé]|espresso|latte|barista|coffee shop)\b/i.test(raw)) {
    return buildRichProduct(brief, {
      category: 'coffee',
      offer: 'coffee',
      defaultBrand: 'Corner Coffee',
      accent: '#7c2d12',
      ink: '#1c1917',
      header: '#faf6f1',
    });
  }
  if (/\b(restaurant|bakery|diner|bistro|kitchen)\b/i.test(raw)) {
    return buildRichProduct(brief, {
      category: 'food',
      offer: 'food',
      defaultBrand: 'Kitchen',
      accent: '#7c2d12',
      ink: '#1c1917',
      header: '#faf6f1',
    });
  }
  if (/\b(shop|store|boutique|marketplace|ecommerce|e-commerce)\b/i.test(raw)) {
    return buildRichProduct(brief, {
      category: 'shop',
      offer: 'shop',
      defaultBrand: 'Shop',
      accent: '#9a3412',
      ink: '#1c1917',
      header: '#fff7ed',
    });
  }
  if (/\b(school|course|class|lesson|tutor|university)\b/i.test(raw)) {
    return buildRichProduct(brief, {
      category: 'school',
      offer: 'school',
      defaultBrand: 'School',
      accent: '#1e3a8a',
      ink: '#172033',
      header: '#f4f7fb',
    });
  }
  if (/\b(clinic|doctor|dentist|hospital|pharmacy)\b/i.test(raw)) {
    return buildRichProduct(brief, {
      category: 'clinic',
      offer: 'clinic',
      defaultBrand: 'Clinic',
      accent: '#0f766e',
      ink: '#132422',
      header: '#f3faf8',
    });
  }
  if (/\b(saas|software|dashboard|analytics|startup|app|platform)\b/i.test(raw)) {
    return buildRichProduct(brief, {
      category: 'saas',
      offer: 'saas',
      defaultBrand: 'North',
      accent: '#0f766e',
      ink: '#14221f',
      header: '#f3f6f4',
    });
  }
  if (/\b(gym|fitness|workout|yoga|sport|training)\b/i.test(raw)) {
    return buildRichProduct(brief, {
      category: 'fitness',
      offer: 'fitness',
      defaultBrand: 'Studio Fit',
      accent: '#b45309',
      ink: '#1c1917',
      header: '#fffbeb',
    });
  }
  if (/\b(portfolio|resume|cv)\b/i.test(raw)) {
    return buildRichProduct(brief, {
      category: 'portfolio',
      offer: 'portfolio',
      defaultBrand: 'Portfolio',
      accent: '#1e3a8a',
      ink: '#172033',
      header: '#f8fafc',
    });
  }
  return null;
}
function productSource(brief) {
  if (wantsAgent(brief)) return agentSource(brief);
  if (wantsSearch(brief)) return searchSource(brief);
  if (wantsJobs(brief)) return jobSource(brief);
  return offeringSource(brief) || defaultProductSource(brief);
}

function searchBrand(text) {
  const named = explicitBrand(text);
  if (named) return named;
  const corrected = correctKnownNameTypos(text);
  const ref = referenceQuery(corrected);
  if (isSearchName(ref)) return titleName(ref);
  const subject = briefSubject(corrected).replace(/^(?:like|similar to)\s+(?:an?\s+)?/i, '').trim();
  if (isSearchName(subject)) return titleName(subject);
  if (subject && !/^(like|similar)$/i.test(subject)) return titleCase(subject);
  return 'Search Engine';
}

function searchSource(brief) {
  const brand = searchBrand(brief);
  return {
    kind: 'search',
    brand,
    accent: '#0b57d0',
    ink: '#1c1b1f',
    header: '#ffffff',
    nav: ['Search', 'Results', 'Sources', 'Privacy'],
    hero: {
      title: 'Search the web.',
      lead: `Type a question. ${brand} returns the page title, the site it came from, and a line from that page.`,
      cta: 'Search',
    },
    results: [
      { title: 'How a page gets into the results', url: 'https://example.com/ranking', site: 'example.com', snippet: 'A result is the title, the address, and one line from the page that matched the words you typed.' },
      { title: 'Weather in London this afternoon', url: 'https://weather.example/london', site: 'weather.example', snippet: 'Cloud, 14 degrees, and rain after 4. The forecast is the page, not a guess typed into the box.' },
      { title: 'Trains from London to Manchester', url: 'https://rail.example/london-manchester', site: 'rail.example', snippet: 'The next direct train, the platform, and the price, shown before you leave the result.' },
      { title: 'Tomato soup in one pot', url: 'https://kitchen.example/tomato-soup', site: 'kitchen.example', snippet: 'The ingredients, the time on the stove, and the step that makes the soup thick.' },
      { title: 'Public library opening hours', url: 'https://library.example/hours', site: 'library.example', snippet: 'Which branch is open tonight, and the desk that still has a computer free.' },
    ],
    sources: [
      { title: 'Public pages', body: 'Results are pages already on the web. Each row names the site the page came from.' },
      { title: 'The line you can read', body: 'The short line under the title is taken from that page, so you can skip it without opening it.' },
      { title: 'No paid row in this set', body: 'The example results are ordinary pages. A paid placement would be marked as paid.' },
    ],
    privacy: {
      title: 'Privacy',
      body: `The words you type stay in this browser until you search again or leave. ${brand} does not sell those words.`,
    },
  };
}

function jobSource(brief) {
  const brand = shortBrandFromBrief(brief, 'Job Platform');
  const theme = themeFromBrief(brief, { accent: '#0f3d2e', ink: '#14221c', header: '#f4f7f5' });
  return {
    kind: 'jobs',
    brand,
    accent: theme.accent,
    ink: theme.ink,
    header: theme.header,
    nav: ['Jobs', 'Companies', 'Apply'],
    hero: {
      title: 'Find a role. Send an application.',
      lead: `${brand} lists open roles, the companies hiring, and an application you can send from this page.`,
      cta: 'Apply for a role',
    },
    jobs: [
      { title: 'Product designer', company: 'Lumen', place: 'London', type: 'Full time', pay: 'Â£70,000', body: 'Design the screens people use to find a role and send an application.' },
      { title: 'Software engineer', company: 'Fieldwork', place: 'Remote', type: 'Full time', pay: 'Â£85,000', body: 'Build the list of roles, the company pages, and the application form.' },
      { title: 'Recruiter', company: 'Harbor', place: 'Manchester', type: 'Full time', pay: 'Â£45,000', body: 'Read applications and keep the open roles current.' },
    ],
    companies: [
      { title: 'Lumen', roles: '12 open roles', body: 'A product studio in London. A person on the team reads each application.' },
      { title: 'Fieldwork', roles: '8 open roles', body: 'A remote software team. The application goes to the people you would work with.' },
      { title: 'Harbor', roles: '5 open roles', body: 'A hiring team in Manchester. They reply within five working days.' },
    ],
    apply: {
      title: 'Apply',
      lead: 'Leave your name, email, and the role. The company receives it from this page.',
      cta: 'Send application',
      done: 'Application sent. The company has your name and the role you chose.',
    },
  };
}

function bankSource(brief) {
  const brand = bankBrand(brief);
  return {
    kind: 'bank',
    brand,
    accent: '#123c34',
    ink: '#1a2421',
    header: '#f6f3ec',
    mark: '#b08968',
    nav: ['Accounts', 'Cards', 'Transfers', 'Security', 'Fees'],
    hero: {
      title: 'A bank for everyday money.',
      lead: `${brand} holds a current account, savings, and a card. You send a payment only after the fee is on the screen.`,
      cta: 'Open an account',
    },
    preview: {
      last4: '4418',
      expiry: '12/29',
      balances: [
        { label: 'Current', value: 'Â£2,480.20' },
        { label: 'Savings', value: 'Â£9,140.00' },
      ],
    },
    accounts: [
      { title: 'Current', price: 'Â£0 a month', body: 'Get paid, pay bills, and spend from the account you use every day.' },
      { title: 'Savings', price: 'Interest monthly', body: 'Move money aside and move it back when you need it. The rate is shown before you start.' },
      { title: 'Joint', price: 'Two people', body: 'Share rent and household bills. Both of you can see every payment.' },
    ],
    cards: [
      { title: 'Debit card', price: 'In shops and online', body: 'Pay with the card on the current account. Freeze it in the app if it is not with you.' },
      { title: 'Virtual card', price: 'For subscriptions', body: 'Use a separate number online. Close that number when you stop the service.' },
    ],
    transfers: [
      { title: 'Faster Payments', body: 'Send to a UK account on any day. The name is checked before the money leaves.' },
      { title: 'Bills', body: 'Set a standing order for rent, or a direct debit for a bill you already expect.' },
      { title: 'Abroad', body: 'An international payment shows the fee and the rate before you confirm it.' },
    ],
    security: [
      { title: 'It is you', body: 'Sign in with a face, a fingerprint, or a code. A new device asks again.' },
      { title: 'The card can stop', body: 'Freeze spending in the app, then unfreeze it when the card is back with you.' },
      { title: 'You see the payment', body: 'Each card payment can send an alert, so a charge you do not know shows up at once.' },
    ],
    fees: [
      { title: 'Current account', price: 'Â£0', body: 'No monthly fee.' },
      { title: 'UK transfer', price: 'Â£0', body: 'Faster Payments inside the UK.' },
      { title: 'Debit card', price: 'Â£0', body: 'No yearly fee for the card.' },
      { title: 'International payment', price: 'Â£2.50', body: 'Shown with the rate before you send.' },
    ],
    open: {
      title: 'Open an account',
      lead: 'Leave your name and email. The next step arrives there, and nothing is charged on this page.',
      cta: 'Open an account',
      done: 'Application started. Check your email to finish opening the account.',
    },
  };
}

const KNOWN_BRANDS = [
  'Google', 'YouTube', 'Facebook', 'Instagram', 'WhatsApp', 'Amazon', 'Netflix', 'Spotify',
  'Airbnb', 'LinkedIn', 'Twitter', 'TikTok', 'Microsoft', 'Apple', 'Slack', 'Notion',
  'Figma', 'Stripe', 'Shopify', 'Pinterest', 'Reddit', 'Discord', 'Dropbox', 'PayPal',
  'Adobe', 'Canva', 'Wikipedia', 'GitHub', 'GitLab', 'Twitch', 'Snapchat', 'Telegram',
  'Gmail', 'Outlook', 'Chrome', 'Firefox', 'Windows', 'Android', 'Samsung', 'Tesla',
  'Disney', 'Expedia', 'Tripadvisor', 'Kayak', 'DoorDash', 'Duolingo', 'Coursera',
  'WordPress', 'Squarespace', 'Webflow', 'Salesforce', 'HubSpot', 'Trello', 'Asana',
  'Peloton', 'Indeed', 'Upwork', 'Fiverr', 'Zillow', 'OpenAI', 'ChatGPT', 'Starbucks',
  'Walmart', 'Costco', 'Adidas', 'Booking', 'Claude', 'Gemini', 'Copilot', 'iPhone',
  'Pepsi', 'Minecraft', 'Roblox', 'Fortnite', 'PlayStation', 'McDonalds', 'Venmo',
];
const BRAND_KEYS = new Map(KNOWN_BRANDS.map((name) => [name.toLowerCase(), name]));
const COMMON_WORDS = new Set(`about above after again along also among another answer around because before being below between black bring build built business called change children colour color company could course create design different during early email every family first found general given going great group having header house however large later learn leave little local looking making might money modern never number often order other people phone phones place point power public question right school second shall should small something sound still story study system their there these thing think those three through today together under until using value water where which while world would write young your apply ample apples slacks slick stack smack snack strike stripes tripe motion nation lotion discard twister goggle canvas canvass abode interest bookings window windows widows switch clause cloud sigma indent fiver kayaks shine offer start works north built venom willow upward giver booing fortnight please thanks wanted really something website landing homepage another without people around white green please create build design generate called named branded brand bakery shop store class lesson clinic doctor search jobs stays agent bank page site home homes header accent button logo image photo video music news blog post help need want make like this that with from your have into over such than then them some very just only also back well much many even most more made make time year work life good long last find here know take come see want look use any day get`.split(/\s+/));

function singleCharMiss(a, b) {
  if (a === b) return false;
  const la = a.length;
  const lb = b.length;
  if (Math.abs(la - lb) > 1) return false;
  if (la === lb) {
    let first = -1;
    for (let i = 0; i < la; i++) {
      if (a[i] === b[i]) continue;
      if (first === -1) {
        first = i;
        continue;
      }
      if (i === first + 1 && a[first] === b[i] && a[i] === b[first]) {
        for (let k = i + 1; k < la; k++) if (a[k] !== b[k]) return false;
        return true;
      }
      return false;
    }
    return first !== -1;
  }
  const shorter = la < lb ? a : b;
  const longer = la < lb ? b : a;
  let i = 0;
  let j = 0;
  let skipped = false;
  while (i < shorter.length && j < longer.length) {
    if (shorter[i] === longer[j]) {
      i += 1;
      j += 1;
    } else if (!skipped) {
      skipped = true;
      j += 1;
    } else {
      return false;
    }
  }
  if (i !== shorter.length) return false;
  if (j === longer.length) return skipped;
  return !skipped && j === longer.length - 1;
}

function correctKnownNameTypos(text) {
  const raw = String(text || '');
  if (!raw) return raw;
  return raw.replace(/\b[A-Za-z][A-Za-z0-9]{4,24}\b/g, (token) => {
    const lower = token.toLowerCase();
    if (BRAND_KEYS.has(lower) || COMMON_WORDS.has(lower)) return token;
    let match = '';
    for (const [key, name] of BRAND_KEYS) {
      if (!singleCharMiss(lower, key)) continue;
      if (match) return token;
      match = name;
    }
    return match || token;
  });
}

export function createSiteSource(brief) {
  const text = correctKnownNameTypos(brief);
  let source;
  if (wantsBank(text)) source = bankSource(text);
  else if (wantsSearch(text)) source = searchSource(text);
  else if (wantsJobs(text)) source = jobSource(text);
  else if (wantsStays(text)) source = staysSource(text);
  else if (wantsBlog(text)) source = blogSource(text);
  else source = productSource(text) || defaultProductSource(text);
  return sanitizeSourceCopy(source);
}

/** Build a LIVE-preview source from model-generated HTML/CSS/JS. */
export function renderedSourceFromArtifacts({ brand, brief, html, css, js } = {}) {
  const safeBrand = String(brand || 'Site').replace(/\s+/g, ' ').trim().slice(0, 48) || 'Site';
  return {
    kind: 'rendered',
    brand: safeBrand,
    brief: String(brief || '').slice(0, 4000),
    html: String(html || ''),
    css: String(css || ''),
    js: String(js || ''),
  };
}

function looksLikeSiteRevision(text) {
  const t = String(text || '');
  if (!t.trim()) return false;
  return /\b(rename|call it|name it|brand it|change|update|make (it|the)|add|remove|swap|replace|recolor|colour|color|header|accent|section|hero|cta|button|darker|lighter|bigger|smaller|more|less|rewrite|regenerate|tweak|adjust|fix)\b/i.test(t)
    || /^[A-Za-z][\w-]{1,24}(?:\s+[A-Za-z][\w-]{1,24}){0,2}$/.test(t.trim().replace(/[.!?]+$/g, ''));
}

function reviseRenderedSource(source, text) {
  const next = JSON.parse(JSON.stringify(source));
  let changed = false;
  const raw = correctKnownNameTypos(text);
  const renameMatch = raw.match(/\b(?:call it|name it|rename(?:\s+it)?(?:\s+to)?|brand(?:\s+it)?(?:\s+to)?)\s+["']?([A-Za-z][\w-]{1,24})/i)
    || raw.match(/\b(?:change|update|set)\s+(?:the\s+)?(?:name|brand|title)(?:\s+from\s+["']?[\w.\s-]+["']?)?\s+to\s+["']?([A-Za-z][\w-]{1,24})/i)
    || raw.match(/\b(?:new name(?:\s+is)?|make it|switch(?:\s+it)?\s+to)\s+["']?([A-Za-z][\w-]{1,24})/i);
  let renamedTo = renameMatch ? renameMatch[1] : '';
  if (!renamedTo) {
    const bare = String(raw || '').trim().replace(/[.!?]+$/g, '');
    if (
      /^[A-Za-z][\w-]{1,24}(?:\s+[A-Za-z][\w-]{1,24}){0,2}$/.test(bare)
      && !/^(ok|okay|yes|no|thanks|thank you|please|hi|hello|hey|red|black|blue|green|pink|teal|white|orange|purple|yellow|header|accent|menu|product|start|welcome)$/i.test(bare)
      && !isWebsiteRequest(bare)
      && !isChatFollowUp(bare)
      && !COMMON_WORDS.has(bare.toLowerCase())
    ) {
      renamedTo = bare;
    }
  }
  if (renamedTo) {
    const oldBrand = String(next.brand || '');
    const newBrand = /[a-z][A-Z]|[A-Z]{2,}/.test(renamedTo) ? renamedTo : titleCase(renamedTo);
    next.brand = newBrand;
    const replaceBrand = (value) => {
      if (!value) return value;
      let out = String(value);
      if (oldBrand) {
        try {
          out = out.replace(new RegExp(oldBrand.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi'), newBrand);
        } catch (_) { /* keep */ }
      }
      return out;
    };
    next.html = replaceBrand(next.html);
    next.css = replaceBrand(next.css);
    next.js = replaceBrand(next.js);
    changed = true;
  }
  const hex = raw.match(/#(?:[0-9a-f]{3}|[0-9a-f]{6})\b/i);
  const named = raw.match(/\b(red|black|blue|green|pink|teal|white|orange|purple|yellow)\b/i);
  const color = hex ? hex[0] : named ? COLORS[named[1].toLowerCase()] : '';
  if (color && next.css) {
    // Prefer updating CSS custom properties when present.
    let css = String(next.css);
    if (/--accent\s*:/.test(css) && !/\bheader\b/i.test(raw)) {
      css = css.replace(/--accent\s*:\s*[^;]+;/i, `--accent: ${color};`);
      changed = true;
    } else if (/--header\s*:/.test(css) && /\bheader\b/i.test(raw)) {
      css = css.replace(/--header\s*:\s*[^;]+;/i, `--header: ${color};`);
      changed = true;
    } else if (/--accent\s*:/.test(css)) {
      css = css.replace(/--accent\s*:\s*[^;]+;/i, `--accent: ${color};`);
      changed = true;
    }
    next.css = css;
  }
  return changed ? next : null;
}

export function reviseSource(source, text) {
  if (!source) return null;
  if (source.kind === 'rendered') return reviseRenderedSource(source, text);
  const next = JSON.parse(JSON.stringify(source));
  let changed = false;
  const raw = correctKnownNameTypos(text);
  const renameMatch = raw.match(/\b(?:call it|name it|rename(?:\s+it)?(?:\s+to)?|brand(?:\s+it)?(?:\s+to)?)\s+["']?([A-Za-z][\w-]{1,24})/i)
    || raw.match(/\b(?:change|update|set)\s+(?:the\s+)?(?:name|brand|title)(?:\s+from\s+["']?[\w.\s-]+["']?)?\s+to\s+["']?([A-Za-z][\w-]{1,24})/i)
    || raw.match(/\b(?:new name(?:\s+is)?|make it|switch(?:\s+it)?\s+to)\s+["']?([A-Za-z][\w-]{1,24})/i);
  let renamedTo = renameMatch ? renameMatch[1] : '';
  if (!renamedTo) {
    const bare = String(raw || '').trim().replace(/[.!?]+$/g, '');
    if (
      /^[A-Za-z][\w-]{1,24}(?:\s+[A-Za-z][\w-]{1,24}){0,2}$/.test(bare)
      && !/^(ok|okay|yes|no|thanks|thank you|please|hi|hello|hey|red|black|blue|green|pink|teal|white|orange|purple|yellow|header|accent|menu|product|start|welcome)$/i.test(bare)
      && !isWebsiteRequest(bare)
      && !isChatFollowUp(bare)
      && !COMMON_WORDS.has(bare.toLowerCase())
    ) {
      renamedTo = bare;
    }
  }
  if (renamedTo) {
    const oldBrand = String(next.brand || '');
    const newBrand = /[a-z][A-Z]|[A-Z]{2,}/.test(renamedTo) ? renamedTo : titleCase(renamedTo);
    next.brand = newBrand;
    const replaceBrand = (value) => {
      if (!oldBrand || !value) return value;
      try {
        return String(value).replace(new RegExp(oldBrand.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi'), newBrand);
      } catch (_) {
        return value;
      }
    };
    if (next.hero) {
      if (next.hero.title && new RegExp('^' + oldBrand.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '[.!?]?$', 'i').test(String(next.hero.title).trim())) {
        next.hero.title = newBrand + '.';
      } else {
        next.hero.title = replaceBrand(next.hero.title);
      }
      next.hero.lead = replaceBrand(next.hero.lead);
    }
    if (Array.isArray(next.features)) {
      next.features = next.features.map((feature) => ({
        ...feature,
        title: replaceBrand(feature.title),
        body: replaceBrand(feature.body),
      }));
    }
    if (next.about) {
      next.about.title = replaceBrand(next.about.title);
      next.about.body = replaceBrand(next.about.body);
    }
    if (Array.isArray(next.steps)) {
      next.steps = next.steps.map((step) => ({ ...step, title: replaceBrand(step.title), body: replaceBrand(step.body) }));
    }
    if (Array.isArray(next.testimonials)) {
      next.testimonials = next.testimonials.map((item) => ({ ...item, quote: replaceBrand(item.quote) }));
    }
    if (Array.isArray(next.posts)) {
      next.posts = next.posts.map((post) => ({
        ...post,
        title: replaceBrand(post.title),
        excerpt: replaceBrand(post.excerpt),
      }));
    }
    if (Array.isArray(next.pricing)) {
      next.pricing = next.pricing.map((plan) => ({ ...plan, note: replaceBrand(plan.note) }));
    }
    if (next.footer && next.footer.note) next.footer.note = replaceBrand(next.footer.note);
    changed = true;
  }
  const hex = raw.match(/#(?:[0-9a-f]{3}|[0-9a-f]{6})\b/i);
  const named = raw.match(/\b(red|black|blue|green|pink|teal|white|orange|purple|yellow)\b/i);
  const color = hex ? hex[0] : named ? COLORS[named[1].toLowerCase()] : '';
  if (color) {
    if (/\bheader\b/i.test(raw)) {
      next.header = color;
    } else if (named && THEME_FROM_COLOR[named[1].toLowerCase()]) {
      const palette = THEME_FROM_COLOR[named[1].toLowerCase()];
      next.accent = palette.accent;
      next.ink = palette.ink;
      next.header = palette.header;
    } else {
      next.accent = color;
    }
    changed = true;
  }
  if (wantsAgent(raw) && next.offer !== 'agent') {
    const agent = agentSource(raw);
    if (renamedTo) agent.brand = titleCase(renamedTo);
    if (explicitBrand(raw)) agent.brand = explicitBrand(raw);
    if (color) {
      if (/\bheader\b/i.test(raw)) agent.header = color;
      else agent.accent = color;
    }
    return agent;
  }
  if (wantsBank(raw) && next.kind !== 'bank') {
    const bank = bankSource(raw);
    const explicit = explicitBrand(raw);
    if (explicit) bank.brand = explicit;
    else if (renamedTo) bank.brand = titleCase(renamedTo);
    else if (source.brand && !/^(north|welcome)$/i.test(source.brand)) bank.brand = source.brand;
    if (color) {
      if (/\bheader\b/i.test(raw)) bank.header = color;
      else bank.accent = color;
    }
    return bank;
  }
  if (wantsSearch(raw) && next.kind !== 'search') {
    const search = searchSource(raw);
    const explicit = explicitBrand(raw);
    if (explicit) search.brand = explicit;
    else if (renamedTo) search.brand = titleCase(renamedTo);
    else if (source.brand && !/^(north|welcome)$/i.test(source.brand)) search.brand = source.brand;
    if (color) {
      if (/\bheader\b/i.test(raw)) search.header = color;
      else search.accent = color;
    }
    return search;
  }
  if (wantsJobs(raw) && next.kind !== 'jobs') {
    const jobs = jobSource(raw);
    const explicit = explicitBrand(raw);
    if (explicit) jobs.brand = explicit;
    else if (renamedTo) jobs.brand = titleCase(renamedTo);
    else if (source.brand && !/^(north|welcome)$/i.test(source.brand)) jobs.brand = source.brand;
    if (color) {
      if (/\bheader\b/i.test(raw)) jobs.header = color;
      else jobs.accent = color;
    }
    return jobs;
  }
  if (wantsStays(raw) && next.kind !== 'stays') {
    const stayed = staysSource(raw);
    stayed.brand = next.brand;
    return stayed;
  }
  if (wantsBlog(raw) && next.offer !== 'blog') {
    const blog = blogSource(raw);
    const explicit = explicitBrand(raw);
    if (explicit) blog.brand = explicit;
    else if (next.brand && !looksLikePastedBrief(next.brand) && !/^(studio|welcome|north)$/i.test(next.brand)) {
      blog.brand = next.brand;
    }
    if (color) {
      if (/\bheader\b/i.test(raw)) blog.header = color;
      else blog.accent = color;
    }
    return sanitizeSourceCopy(blog);
  }
  return changed ? sanitizeSourceCopy(next) : null;
}
export function isSiteFollowUp(text, source) {
  if (!source) return false;
  if (isWebsiteRequest(text)) return false;
  if (source.kind === 'rendered' && looksLikeSiteRevision(text)) return true;
  return reviseSource(source, text) != null;
}

function staysCss(source) {
  return `@import url('https://fonts.googleapis.com/css2?family=Nunito:wght@560;700;800&display=swap');
:root { --accent: ${source.accent}; --ink: ${source.ink}; --header: ${source.header}; --line: #dddddd; --muted: #6a6a6a; }
* { box-sizing: border-box; }
html, body { margin: 0; background: var(--header); color: var(--ink); font-family: Nunito, "Segoe UI", sans-serif; }
button, input { font: inherit; }
.topbar { position: sticky; top: 0; z-index: 2; display: flex; align-items: center; gap: 24px; padding: 16px 32px; background: var(--header); border-bottom: 1px solid #ebebeb; }
.logo { font-weight: 800; font-size: 28px; letter-spacing: -0.06em; color: var(--accent); }
.search { flex: 1; display: flex; align-items: center; max-width: 760px; margin: 0 auto; border: 1px solid var(--line); border-radius: 40px; box-shadow: 0 3px 12px rgba(0,0,0,.08); overflow: hidden; background: #fff; }
.search label { display: flex; flex-direction: column; gap: 2px; padding: 10px 18px; }
.search label + label { border-left: 1px solid var(--line); }
.search span { font-size: 12px; font-weight: 800; }
.search input { border: 0; outline: 0; width: 130px; background: transparent; color: var(--ink); }
.go { width: 44px; height: 44px; margin: 6px; border: 0; border-radius: 50%; background: var(--accent); color: #fff; cursor: pointer; }
.cats { display: flex; gap: 22px; padding: 16px 32px 0; overflow-x: auto; border-bottom: 1px solid #ebebeb; }
.cat { border: 0; background: transparent; color: var(--muted); padding: 0 0 12px; cursor: pointer; font-weight: 800; border-bottom: 2px solid transparent; }
.cat.on { color: var(--ink); border-bottom-color: var(--ink); }
.cards { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 22px 16px; padding: 22px 32px 48px; }
.card { cursor: pointer; }
.shot { position: relative; aspect-ratio: 1 / 0.96; border-radius: 14px; overflow: hidden; background: #eee; }
.shot img { width: 100%; height: 100%; object-fit: cover; display: block; }
.heart { position: absolute; top: 10px; right: 10px; border: 0; background: transparent; color: #fff; font-size: 20px; cursor: pointer; text-shadow: 0 1px 3px rgba(0,0,0,.45); }
.heart.on { color: var(--accent); }
.meta { display: flex; justify-content: space-between; margin-top: 10px; font-weight: 800; }
.sub, .price { margin: 2px 0 0; color: var(--muted); font-weight: 700; }
.price b { color: var(--ink); }
.foot { display: flex; justify-content: space-between; padding: 22px 32px 36px; border-top: 1px solid #ebebeb; color: var(--muted); font-size: 13px; font-weight: 800; }
.empty { color: var(--muted); font-weight: 800; }
@media (max-width: 1100px) { .cards { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (max-width: 720px) { .cards { grid-template-columns: 1fr; } .topbar, .cats, .cards, .foot { padding-left: 16px; padding-right: 16px; } }
`;
}

function staysData(source) {
  return {
    brand: source.brand,
    categories: source.categories,
    listings: source.listings,
  };
}

function staysJs(source) {
  return `// listings.json is this same data, written from system.json.
const source = ${JSON.stringify(staysData(source))};
const saved = {};
let active = 'All';
const catsEl = document.getElementById('cats');
const grid = document.getElementById('grid');
source.categories.forEach((name) => {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'cat' + (name === active ? ' on' : '');
  btn.textContent = name;
  btn.onclick = () => { active = name; draw(); };
  catsEl.appendChild(btn);
});
function draw() {
  const q = (document.getElementById('where').value || '').trim().toLowerCase();
  Array.from(catsEl.children).forEach((btn) => {
    btn.className = 'cat' + (btn.textContent === active ? ' on' : '');
  });
  const list = source.listings.filter((stay) => {
    const hit = !q || (stay.place + ' ' + stay.title).toLowerCase().indexOf(q) >= 0;
    return hit && (active === 'All' || stay.cat === active);
  });
  grid.innerHTML = '';
  if (!list.length) {
    const empty = document.createElement('p');
    empty.className = 'empty';
    empty.textContent = 'No stays match that search.';
    grid.appendChild(empty);
    return;
  }
  list.forEach((stay) => {
    const card = document.createElement('article');
    card.className = 'card';
    card.innerHTML = '<div class="shot"><img alt="" src="' + stay.img + '" /><button class="heart' + (saved[stay.title] ? ' on' : '') + '" type="button">â™¥</button></div><div class="meta"><span>' + stay.place + '</span><span>â˜… ' + stay.rating + '</span></div><p class="sub">' + stay.title + '</p><p class="price"><b>Â£' + stay.price + '</b> night</p>';
    card.querySelector('.heart').onclick = (event) => {
      event.stopPropagation();
      saved[stay.title] = !saved[stay.title];
      draw();
    };
    grid.appendChild(card);
  });
}
document.getElementById('search').onsubmit = (event) => { event.preventDefault(); draw(); };
document.getElementById('where').oninput = draw;
draw();
`;
}

function siteName(source) {
  const name = String(source.brand || '').trim();
  return name || 'site';
}

function siteSlug(source) {
  const slug = siteName(source).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  return slug || 'site';
}

function siteSummary(source) {
  const name = siteName(source);
  if (source.kind === 'stays') {
    const count = Array.isArray(source.listings) ? source.listings.length : 0;
    return `${name} is a stay search page with ${count} homes. People look up a place, choose a category, and open a home for a few nights.`;
  }
  if (source.kind === 'bank') {
    const accounts = joinList((source.accounts || []).map((item) => item.title));
    return `${name} is a digital bank. People compare ${accounts}, look at cards, transfers, security, and fees, then open an account.`;
  }
  if (source.kind === 'search') {
    const count = Array.isArray(source.results) ? source.results.length : 0;
    return `${name} is a search engine. Type a query, then read ${count} result rows with a title, a site, and a line from the page.`;
  }
  if (source.kind === 'jobs') {
    const count = Array.isArray(source.jobs) ? source.jobs.length : 0;
    return `${name} is a job platform. People browse ${count} roles, read the companies, and apply.`;
  }
  if (source.kind === 'studied') {
    return `${name} is a page shaped from a public site. ${source.hero?.lead || ''}`.replace(/\s+/g, ' ').trim();
  }
  const headline = String(source.hero?.title || '').trim();
  const lead = String(source.hero?.lead || '').trim();
  return `${name} is a product page. ${headline} ${lead}`.replace(/\s+/g, ' ').trim();
}

function pageTitle(source) {
  if (source.kind === 'stays' || source.kind === 'bank' || source.kind === 'search' || source.kind === 'jobs' || source.kind === 'studied') return siteName(source);
  return String(source.hero?.title || siteName(source)).trim() || siteName(source);
}

function documentShell(source, body) {
  const title = escapeHtml(pageTitle(source));
  const description = escapeHtml(siteSummary(source));
  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>${title}</title>
  <meta name="description" content="${description}" />
  <link rel="stylesheet" href="styles.css" />
</head>
<body>
${body}
  <script src="app.js"></script>
</body>
</html>
`;
}

function staysHtml(source) {
  const brand = escapeHtml(source.brand);
  return documentShell(source, `<header class="topbar">
  <div class="logo">${brand}</div>
  <form class="search" id="search">
    <label><span>Where</span><input id="where" placeholder="Search destinations" /></label>
    <label><span>Check in</span><input type="date" /></label>
    <label><span>Check out</span><input type="date" /></label>
    <label><span>Who</span><input placeholder="Add guests" /></label>
    <button class="go" type="submit" aria-label="Search">âŒ•</button>
  </form>
</header>
<nav class="cats" id="cats"></nav>
<main class="cards" id="grid"></main>
<footer class="foot"><span>Â© ${brand}</span><span>Privacy Â· Terms</span></footer>`);
}

function productCss(source) {
  return `@import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,560;9..144,700&family=Outfit:wght@400;520;650;750&display=swap');
:root { --accent: ${source.accent}; --ink: ${source.ink}; --header: ${source.header}; --muted: color-mix(in srgb, var(--ink) 58%, #71717a); --card: #fff; --line: color-mix(in srgb, var(--accent) 14%, transparent); }
* { box-sizing: border-box; }
html { scroll-behavior: smooth; }
body { margin: 0; font-family: Outfit, "Segoe UI", sans-serif; color: var(--ink); background:
  radial-gradient(1200px 480px at 12% -10%, color-mix(in srgb, var(--accent) 12%, transparent), transparent 60%),
  radial-gradient(900px 420px at 90% 8%, color-mix(in srgb, var(--accent) 8%, transparent), transparent 55%),
  var(--header); }
a { color: inherit; }
.topbar { position: sticky; top: 0; z-index: 5; display: flex; align-items: center; justify-content: space-between; gap: 24px; padding: 18px 48px; background: color-mix(in srgb, var(--header) 88%, #fff); border-bottom: 1px solid var(--line); backdrop-filter: blur(12px); }
.logo { font-weight: 750; font-size: 22px; letter-spacing: -0.045em; color: var(--accent); max-width: min(42vw, 280px); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.nav { display: flex; flex-wrap: wrap; justify-content: flex-end; gap: 8px 20px; }
.nav a { color: var(--muted); text-decoration: none; font-weight: 560; font-size: 14px; }
.nav a:hover { color: var(--accent); }
.hero { min-height: calc(100vh - 72px); display: grid; grid-template-columns: 1.05fr .95fr; gap: 56px; align-items: center; padding: 88px 64px 72px; }
.kicker { margin: 0 0 14px; font-size: 12px; font-weight: 650; letter-spacing: .16em; text-transform: uppercase; color: var(--accent); }
.hero h1 { margin: 0; font-family: Fraunces, Georgia, serif; font-size: clamp(44px, 6.2vw, 76px); line-height: 1.02; letter-spacing: -0.045em; font-weight: 560; max-width: 12ch; }
.lead { margin: 18px 0 0; font-size: 19px; max-width: 38rem; line-height: 1.55; color: var(--muted); }
.hero-actions { display: flex; flex-wrap: wrap; gap: 12px; align-items: center; margin-top: 28px; }
.cta { display: inline-flex; align-items: center; justify-content: center; background: var(--accent); color: #fff; text-decoration: none; padding: 15px 26px; border-radius: 999px; font-weight: 700; box-shadow: 0 14px 30px color-mix(in srgb, var(--accent) 26%, transparent); }
.cta:hover { filter: brightness(0.96); }
.cta.ghost { background: transparent; color: var(--accent); border: 1px solid color-mix(in srgb, var(--accent) 35%, transparent); box-shadow: none; }
.hero-visual { position: relative; }
.hero img { width: 100%; height: min(560px, 70vh); object-fit: cover; border-radius: 28px; display: block; box-shadow: 0 28px 60px rgba(0,0,0,.12); }
.proof { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 16px; padding: 8px 64px 56px; }
.proof article { background: color-mix(in srgb, #fff 82%, var(--header)); border: 1px solid var(--line); border-radius: 18px; padding: 20px 22px; }
.proof strong { display: block; font-family: Fraunces, Georgia, serif; font-size: 34px; letter-spacing: -0.04em; color: var(--accent); }
.proof span { display: block; margin-top: 6px; color: var(--muted); font-size: 14px; font-weight: 520; }
.section { padding: 72px 64px; }
.section-head { max-width: 40rem; margin-bottom: 28px; }
.section-head h2 { margin: 0; font-family: Fraunces, Georgia, serif; font-size: clamp(32px, 4vw, 44px); letter-spacing: -0.04em; font-weight: 560; }
.section-head p { margin: 12px 0 0; color: var(--muted); font-size: 17px; line-height: 1.5; }
.band { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 18px; }
.band article { background: var(--card); border-radius: 20px; padding: 24px; min-height: 160px; border: 1px solid var(--line); box-shadow: 0 12px 28px rgba(0,0,0,.04); }
.band h3, .band h2 { margin: 0 0 10px; font-size: 20px; color: var(--accent); letter-spacing: -0.02em; }
.band p { margin: 0; color: var(--muted); line-height: 1.5; }
.steps { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 18px; }
.steps article { padding: 24px; border-radius: 20px; background: color-mix(in srgb, #fff 70%, var(--header)); border: 1px solid var(--line); }
.steps .num { font-family: Fraunces, Georgia, serif; font-size: 28px; color: var(--accent); margin-bottom: 10px; }
.steps h3 { margin: 0 0 8px; font-size: 18px; }
.steps p { margin: 0; color: var(--muted); line-height: 1.5; }
.about { display: grid; grid-template-columns: 1.1fr .9fr; gap: 36px; align-items: start; }
.about-card { background: var(--card); border-radius: 24px; padding: 28px; border: 1px solid var(--line); box-shadow: 0 16px 36px rgba(0,0,0,.05); }
.quotes { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 18px; }
.quotes blockquote { margin: 0; background: var(--card); border-radius: 20px; padding: 24px; border: 1px solid var(--line); }
.quotes p { margin: 0 0 16px; font-size: 17px; line-height: 1.5; }
.quotes footer { color: var(--muted); font-size: 14px; font-weight: 560; }
.pricing { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 18px; }
.price { background: var(--card); border-radius: 22px; padding: 26px; border: 1px solid var(--line); display: flex; flex-direction: column; gap: 10px; min-height: 240px; }
.price.featured { border-color: color-mix(in srgb, var(--accent) 45%, transparent); box-shadow: 0 18px 40px color-mix(in srgb, var(--accent) 16%, transparent); }
.price h3 { margin: 0; font-size: 18px; }
.price .amount { font-family: Fraunces, Georgia, serif; font-size: 40px; letter-spacing: -0.04em; color: var(--accent); }
.price p { margin: 0; color: var(--muted); flex: 1; }
.posts { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 18px; }
.posts article { background: var(--card); border-radius: 20px; padding: 22px; border: 1px solid var(--line); }
.posts .date { color: var(--accent); font-size: 12px; font-weight: 650; letter-spacing: .08em; text-transform: uppercase; }
.posts h3 { margin: 10px 0 8px; font-size: 20px; letter-spacing: -0.02em; }
.posts p { margin: 0; color: var(--muted); line-height: 1.5; }
.foot { display: flex; flex-wrap: wrap; justify-content: space-between; gap: 16px; padding: 28px 64px 48px; color: var(--muted); border-top: 1px solid var(--line); margin-top: 24px; }
.foot strong { color: var(--accent); }
.foot-nav { display: flex; flex-wrap: wrap; gap: 14px; }
.foot-nav a { text-decoration: none; font-weight: 560; }
@media (max-width: 900px) {
  .hero, .about, .proof, .steps, .pricing, .posts, .quotes { grid-template-columns: 1fr; }
  .topbar, .hero, .section, .proof, .foot { padding-left: 20px; padding-right: 20px; }
  .logo { max-width: 48vw; }
  .hero { min-height: auto; padding-top: 48px; gap: 28px; }
  .hero img { height: 360px; }
}
`;
}

function productData(source) {
  return {
    brand: source.brand,
    nav: source.nav,
    hero: source.hero,
    features: source.features,
    proof: source.proof || [],
    testimonials: source.testimonials || [],
    steps: source.steps || [],
    about: source.about || null,
    pricing: source.pricing || [],
    posts: source.posts || [],
    footer: source.footer || null,
  };
}

function productJs(source) {
  return `// features.json is this same copy, written from system.json.
const source = ${JSON.stringify(productData(source))};
document.querySelectorAll('[data-feature]').forEach((node, index) => {
  const feature = source.features[index];
  if (!feature) return;
  const title = node.querySelector('h2, h3');
  const body = node.querySelector('p');
  if (title) title.textContent = feature.title;
  if (body) body.textContent = feature.body;
});
const cta = document.querySelector('[data-cta]');
if (cta && source.hero && source.hero.cta) cta.textContent = source.hero.cta;
document.querySelectorAll('[data-brand]').forEach((node) => { node.textContent = source.brand; });
`;
}

function productHtml(source) {
  const brand = escapeHtml(source.brand);
  const cta = escapeHtml(source.hero?.cta || 'Get started');
  const image = source.image || heroImageFromBrief(source.brand || 'studio');
  const nav = source.nav || [];
  const features = source.features || [];
  const proof = source.proof || [];
  const testimonials = source.testimonials || [];
  const steps = source.steps || [];
  const about = source.about || null;
  const pricing = source.pricing || [];
  const posts = source.posts || [];
  const footer = source.footer || { note: brand, links: nav };
  const navHref = (label, index) => {
    const key = String(label || '').toLowerCase();
    if (/post/.test(key)) return '#posts';
    if (/pric|plan|fee|enrol|join|order|book|open|contact/.test(key)) {
      if (pricing.length) return '#pricing';
      if (/book|enrol|join|order|open|contact/.test(key)) return '#about';
    }
    if (/how|process|visit|schedule|task|lesson|care|program|trip|account|goods|menu|product|work|agent/.test(key) && index === 0) return '#features';
    if (/how|process|step/.test(key)) return '#steps';
    if (/proof|visit|task|safety/.test(key)) return proof.length ? '#proof' : '#features';
    if (/about|home/.test(key)) return about ? '#about' : '#features';
    if (features[index]?.id) return '#' + features[index].id;
    return '#features';
  };
  const links = nav.map((item, index) => `<a href="${escapeHtml(navHref(item, index))}">${escapeHtml(item)}</a>`).join('');
  const cards = features.map((feature, index) => {
    const id = feature.id || ('feature-' + index);
    return `<article id="${escapeHtml(id)}" data-feature="${index}"><h3>${escapeHtml(feature.title)}</h3><p>${escapeHtml(feature.body)}</p></article>`;
  }).join('');
  const proofHtml = proof.length ? `<section class="proof" id="proof">${proof.map((item) => `<article><strong>${escapeHtml(item.value)}</strong><span>${escapeHtml(item.label)}</span></article>`).join('')}</section>` : '';
  const stepsHtml = steps.length ? `<section class="section" id="steps"><div class="section-head"><h2>How it works</h2><p>Three clear moves from the first visit to the first result.</p></div><div class="steps">${steps.map((step, index) => `<article><div class="num">0${index + 1}</div><h3>${escapeHtml(step.title)}</h3><p>${escapeHtml(step.body)}</p></article>`).join('')}</div></section>` : '';
  const aboutHtml = about ? `<section class="section about" id="about"><div class="section-head"><h2>${escapeHtml(about.title)}</h2><p>${escapeHtml(about.body)}</p></div><div class="about-card"><h3>Ready when you are</h3><p>${escapeHtml(source.hero?.lead || about.body)}</p><div class="hero-actions"><a class="cta" href="#features">${cta}</a></div></div></section>` : '';
  const quotesHtml = testimonials.length ? `<section class="section" id="stories"><div class="section-head"><h2>What people say</h2><p>Short notes from people who already use ${brand}.</p></div><div class="quotes">${testimonials.map((item) => `<blockquote><p>“${escapeHtml(item.quote)}”</p><footer>${escapeHtml(item.name)} · ${escapeHtml(item.role)}</footer></blockquote>`).join('')}</div></section>` : '';
  const pricingHtml = pricing.length ? `<section class="section" id="pricing"><div class="section-head"><h2>Pricing</h2><p>Start small. Expand when the workflow earns its keep.</p></div><div class="pricing">${pricing.map((plan, index) => `<article class="price${index === 1 ? ' featured' : ''}"><h3>${escapeHtml(plan.name)}</h3><div class="amount">${escapeHtml(plan.price)}</div><p>${escapeHtml(plan.note)}</p><a class="cta${index === 1 ? '' : ' ghost'}" href="#about">${escapeHtml(plan.cta || 'Choose plan')}</a></article>`).join('')}</div></section>` : '';
  const postsHtml = posts.length ? `<section class="section" id="posts"><div class="section-head"><h2>Latest posts</h2><p>Fresh notes from ${brand}.</p></div><div class="posts">${posts.map((post) => `<article><div class="date">${escapeHtml(post.date)}</div><h3>${escapeHtml(post.title)}</h3><p>${escapeHtml(post.excerpt)}</p></article>`).join('')}</div></section>` : '';
  const footLinks = (footer.links || nav).map((item, index) => `<a href="${escapeHtml(navHref(item, index))}">${escapeHtml(item)}</a>`).join('');
  return documentShell(source, `<header class="topbar"><div class="logo" data-brand>${brand}</div><nav class="nav">${links}</nav></header>
<section class="hero">
  <div>
    <p class="kicker" data-brand>${brand}</p>
    <h1 data-hero-title>${escapeHtml(source.hero?.title || brand)}</h1>
    <p class="lead" data-hero-lead>${escapeHtml(source.hero?.lead || '')}</p>
    <div class="hero-actions">
      <a class="cta" href="#features" data-cta>${cta}</a>
      <a class="cta ghost" href="${pricing.length ? '#pricing' : '#steps'}">${pricing.length ? 'See pricing' : 'See how it works'}</a>
    </div>
  </div>
  <div class="hero-visual"><img alt="" src="${escapeHtml(image)}" /></div>
</section>
${proofHtml}
<section class="section" id="features">
  <div class="section-head"><h2>What ${brand} offers</h2><p>Concrete pieces of the product, written from the brief — not filler cards.</p></div>
  <div class="band">${cards}</div>
</section>
${stepsHtml}
${aboutHtml}
${quotesHtml}
${pricingHtml}
${postsHtml}
<footer class="foot"><div><strong data-brand>${brand}</strong><div>${escapeHtml(footer.note || '')}</div></div><nav class="foot-nav">${footLinks}</nav></footer>`);
}
function bankCss(source) {
  return `@import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,560;9..144,640&family=Outfit:wght@400;520;650&display=swap');
:root { --accent: ${source.accent}; --ink: ${source.ink}; --header: ${source.header}; --mark: ${source.mark || '#b08968'}; }
* { box-sizing: border-box; }
html { scroll-behavior: smooth; }
body { margin: 0; color: var(--ink); background: var(--header); font-family: Outfit, "Segoe UI", sans-serif; }
a { color: inherit; }
button, input { font: inherit; }
.topbar { position: sticky; top: 0; z-index: 3; display: flex; align-items: center; gap: 28px; padding: 16px 40px; background: rgba(246,243,236,.94); border-bottom: 1px solid rgba(18,60,52,.08); backdrop-filter: blur(10px); }
.logo { font-weight: 650; letter-spacing: -0.04em; font-size: 20px; color: var(--accent); text-decoration: none; }
.nav { display: flex; gap: 18px; margin-right: auto; }
.nav a { text-decoration: none; font-weight: 520; color: #3d4a46; }
.nav a:hover { color: var(--accent); }
.cta { display: inline-flex; align-items: center; justify-content: center; background: var(--accent); color: #f6f3ec; text-decoration: none; border: 0; border-radius: 999px; padding: 12px 18px; font-weight: 650; cursor: pointer; }
.cta.light { background: transparent; color: var(--accent); border: 1px solid rgba(18,60,52,.25); }
.hero { min-height: 100vh; display: grid; grid-template-columns: 1.05fr .95fr; gap: 48px; align-items: center; padding: 96px 64px; }
.kicker { margin: 0 0 12px; color: var(--mark); font-weight: 650; letter-spacing: .14em; text-transform: uppercase; font-size: 12px; }
.hero h1 { margin: 0; font-family: Fraunces, Georgia, serif; font-size: clamp(64px, 7vw, 80px); line-height: .96; letter-spacing: -0.045em; font-weight: 560; }
.lead { max-width: 460px; font-size: 18px; line-height: 1.45; color: #3d4a46; }
.hero-actions { display: flex; gap: 12px; align-items: center; margin-top: 8px; }
.panel { background: #fff; border-radius: 28px; padding: 22px; box-shadow: 0 24px 50px rgba(18,60,52,.08); }
.plastic { background: linear-gradient(145deg, var(--accent), #0c241f 70%); color: #f6f3ec; border-radius: 22px; min-height: 210px; padding: 22px; display: flex; flex-direction: column; justify-content: space-between; }
.plastic-top, .plastic-foot, .balance { display: flex; justify-content: space-between; gap: 12px; }
.plastic-num { font-family: Fraunces, Georgia, serif; font-size: 28px; letter-spacing: .08em; }
.balances { list-style: none; margin: 16px 0 0; padding: 0; }
.balance { padding: 12px 2px; border-top: 1px solid rgba(18,60,52,.08); font-weight: 520; }
.balance b { font-family: Fraunces, Georgia, serif; font-size: 22px; font-weight: 560; }
.section { padding: 28px 40px; }
.section h2 { margin: 0 0 8px; font-family: Fraunces, Georgia, serif; font-size: 36px; font-weight: 560; letter-spacing: -0.04em; }
.section > p, .open p { margin: 0 0 18px; max-width: 560px; color: #3d4a46; }
.grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 14px; }
.grid.two { grid-template-columns: 1.2fr .8fr; }
.card, .fee { background: #fff; border-radius: 18px; padding: 20px; box-shadow: 0 10px 30px rgba(18,60,52,.05); }
.card h3, .fee h3 { margin: 0 0 6px; font-size: 18px; }
.price { margin: 0 0 8px; color: var(--accent); font-weight: 650; }
.card p, .fee p { margin: 0; color: #3d4a46; }
.card .price, .fee .price { margin: 0 0 8px; color: var(--accent); font-weight: 650; }
.card { cursor: pointer; border: 1px solid transparent; }
.card.on { border-color: var(--accent); }
.fees { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; }
.open { margin: 10px 40px 36px; padding: 28px; border-radius: 28px; background: var(--accent); color: #f6f3ec; display: grid; grid-template-columns: 1fr 1fr; gap: 24px; align-items: center; }
.open h2 { margin: 0 0 8px; font-family: Fraunces, Georgia, serif; font-size: 40px; font-weight: 560; letter-spacing: -0.04em; }
.open p { color: rgba(246,243,236,.82); }
form { display: grid; gap: 10px; }
input { border: 0; border-radius: 12px; padding: 14px 16px; background: #fff; color: var(--ink); }
form .cta { background: var(--mark); color: #1a2421; }
.done { min-height: 1.2em; font-weight: 650; }
.foot { display: flex; justify-content: space-between; gap: 12px; padding: 8px 40px 32px; color: #66716d; font-weight: 520; }
@media (max-width: 980px) {
  .hero, .grid, .grid.two, .fees, .open { grid-template-columns: 1fr; }
  .topbar { flex-wrap: wrap; }
  .nav { width: 100%; margin-right: 0; flex-wrap: wrap; }
  .topbar, .hero, .section, .foot { padding-left: 18px; padding-right: 18px; }
  .open { margin-left: 18px; margin-right: 18px; }
}
`;
}

function bankData(source) {
  return {
    brand: source.brand,
    nav: source.nav,
    hero: source.hero,
    preview: source.preview,
    accounts: source.accounts,
    cards: source.cards,
    transfers: source.transfers,
    security: source.security,
    fees: source.fees,
    open: source.open,
  };
}

function bankJs(source) {
  return `// bank.json is this same bank, written from system.json.
const source = ${JSON.stringify(bankData(source))};
const brand = source.brand;
document.querySelectorAll('[data-brand]').forEach((node) => { node.textContent = brand; });
const heroTitle = document.querySelector('[data-hero-title]');
const heroLead = document.querySelector('[data-hero-lead]');
if (heroTitle) heroTitle.textContent = source.hero.title;
if (heroLead) heroLead.textContent = source.hero.lead;
document.querySelectorAll('[data-cta]').forEach((node) => { node.textContent = source.hero.cta; });
function fill(selector, items) {
  document.querySelectorAll(selector).forEach((node, index) => {
    const item = items[index];
    if (!item) return;
    const title = node.querySelector('h3');
    const price = node.querySelector('.price');
    const body = node.querySelector('p:last-child');
    if (title) title.textContent = item.title;
    if (price) price.textContent = item.price || '';
    if (body) body.textContent = item.body;
  });
}
fill('[data-account]', source.accounts);
fill('[data-card]', source.cards);
fill('[data-transfer]', source.transfers);
fill('[data-security]', source.security);
fill('[data-fee]', source.fees);
document.querySelectorAll('[data-account]').forEach((node) => {
  node.onclick = () => {
    document.querySelectorAll('[data-account]').forEach((item) => item.classList.remove('on'));
    node.classList.add('on');
  };
});
const balances = document.querySelector('[data-balances]');
if (balances) {
  balances.innerHTML = '';
  source.preview.balances.forEach((item) => {
    const row = document.createElement('li');
    row.className = 'balance';
    const label = document.createElement('span');
    label.textContent = item.label;
    const value = document.createElement('b');
    value.textContent = item.value;
    row.append(label, value);
    balances.appendChild(row);
  });
}
const number = document.querySelector('[data-last4]');
if (number) number.textContent = 'â€¢â€¢â€¢â€¢ â€¢â€¢â€¢â€¢ â€¢â€¢â€¢â€¢ ' + source.preview.last4;
const expiry = document.querySelector('[data-expiry]');
if (expiry) expiry.textContent = source.preview.expiry;
const form = document.getElementById('open-form');
const done = document.getElementById('opened');
if (form) {
  form.onsubmit = (event) => {
    event.preventDefault();
    const person = (document.getElementById('person') || {}).value || '';
    if (!String(person).trim()) return;
    if (done) {
      done.hidden = false;
      done.textContent = source.open.done;
    }
    form.reset();
  };
}
`;
}

function sectionIntro(title, lead) {
  return `<h2>${escapeHtml(title)}</h2>${lead ? `<p>${escapeHtml(lead)}</p>` : ''}`;
}

function bankHtml(source) {
  const brand = escapeHtml(source.brand);
  const links = source.nav.map((item) => {
    const href = item === 'Accounts' ? '#accounts'
      : item === 'Cards' ? '#cards'
      : item === 'Transfers' ? '#transfers'
      : item === 'Security' ? '#security'
      : item === 'Fees' ? '#fees'
      : '#open';
    return `<a href="${href}">${escapeHtml(item)}</a>`;
  }).join('');
  const fact = (item, attr, withPrice) => `<article class="${attr === 'data-fee' ? 'fee' : 'card'}" ${attr}><h3>${escapeHtml(item.title)}</h3>${withPrice ? `<p class="price">${escapeHtml(item.price || '')}</p>` : ''}<p>${escapeHtml(item.body)}</p></article>`;
  const accounts = source.accounts.map((item) => fact(item, 'data-account', true)).join('');
  const cards = source.cards.map((item) => fact(item, 'data-card', true)).join('');
  const transfers = source.transfers.map((item) => fact(item, 'data-transfer', false)).join('');
  const security = source.security.map((item) => fact(item, 'data-security', false)).join('');
  const fees = source.fees.map((item) => fact(item, 'data-fee', true)).join('');
  const balances = source.preview.balances.map((item) => `<li class="balance"><span>${escapeHtml(item.label)}</span><b>${escapeHtml(item.value)}</b></li>`).join('');
  return documentShell(source, `<header class="topbar">
  <a class="logo" href="#top" data-brand>${brand}</a>
  <nav class="nav">${links}</nav>
  <a class="cta" href="#open" data-cta>${escapeHtml(source.hero.cta)}</a>
</header>
<section class="hero" id="top">
  <div>
    <p class="kicker" data-brand>${brand}</p>
    <h1 data-hero-title>${escapeHtml(source.hero.title)}</h1>
    <p class="lead" data-hero-lead>${escapeHtml(source.hero.lead)}</p>
    <div class="hero-actions">
      <a class="cta" href="#open" data-cta>${escapeHtml(source.hero.cta)}</a>
      <a class="cta light" href="#fees">See fees</a>
    </div>
  </div>
  <aside class="panel">
    <div class="plastic">
      <div class="plastic-top"><span data-brand>${brand}</span><span>Debit</span></div>
      <div class="plastic-num" data-last4>â€¢â€¢â€¢â€¢ â€¢â€¢â€¢â€¢ â€¢â€¢â€¢â€¢ ${escapeHtml(source.preview.last4)}</div>
      <div class="plastic-foot"><span>Current account</span><span data-expiry>${escapeHtml(source.preview.expiry)}</span></div>
    </div>
    <ul class="balances" data-balances>${balances}</ul>
  </aside>
</section>
<section class="section" id="accounts">${sectionIntro('Accounts', 'Three ways to hold money. Pick one to start, or open all three.')}<div class="grid">${accounts}</div></section>
<section class="section" id="cards">${sectionIntro('Cards', 'A debit card for spending, and a virtual card for anything you can cancel.')}<div class="grid two">${cards}</div></section>
<section class="section" id="transfers">${sectionIntro('Transfers and payments', 'Move money in the UK, pay a bill, or send money abroad after you see the fee.')}<div class="grid">${transfers}</div></section>
<section class="section" id="security">${sectionIntro('Security', 'The account asks who you are, and the card can be stopped without a phone call.')}<div class="grid">${security}</div></section>
<section class="section" id="fees">${sectionIntro('Fees', 'What the account costs before you open it.')}<div class="fees">${fees}</div></section>
<section class="open" id="open">
  <div>
    <h2>${escapeHtml(source.open.title)}</h2>
    <p>${escapeHtml(source.open.lead)}</p>
  </div>
  <form id="open-form">
    <input id="person" name="name" placeholder="Your name" autocomplete="name" />
    <input id="email" name="email" type="email" placeholder="Email" autocomplete="email" />
    <button class="cta" type="submit" data-cta>${escapeHtml(source.open.cta)}</button>
    <p class="done" id="opened" hidden></p>
  </form>
</section>
<footer class="foot"><span>Â© ${brand}</span><span>Accounts Â· Transfers Â· Fees</span></footer>`);
}

function searchCss(source) {
  return `@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;520;650&display=swap');
:root { --accent: ${source.accent}; --ink: ${source.ink}; --header: ${source.header}; }
* { box-sizing: border-box; }
html { scroll-behavior: smooth; }
body { margin: 0; color: var(--ink); background: var(--header); font-family: Outfit, "Segoe UI", sans-serif; }
a { color: inherit; }
button, input { font: inherit; }
.topbar { display: flex; align-items: center; gap: 28px; padding: 18px 40px; }
.logo { font-weight: 650; font-size: 22px; letter-spacing: -0.04em; color: var(--accent); text-decoration: none; }
.nav { display: flex; gap: 18px; }
.nav a { text-decoration: none; color: #44474e; font-weight: 520; }
.nav a:hover { color: var(--accent); }
.home { min-height: 100vh; display: flex; flex-direction: column; justify-content: center; padding: 96px 64px; text-align: center; }
.home h1 { margin: 0; font-size: clamp(64px, 7vw, 80px); letter-spacing: -0.05em; line-height: 1; }
.home .lead { max-width: 560px; margin: 14px auto 0; color: #44474e; font-size: 18px; }
.query { display: flex; gap: 8px; max-width: 680px; margin: 28px auto 0; padding: 8px; border: 1px solid #dadce0; border-radius: 999px; box-shadow: 0 1px 6px rgba(32,33,36,.12); background: #fff; }
.query input { flex: 1; border: 0; outline: 0; padding: 12px 18px; background: transparent; color: var(--ink); font-size: 16px; }
.query button { border: 0; border-radius: 999px; background: var(--accent); color: #fff; padding: 12px 22px; font-weight: 650; cursor: pointer; }
.section { max-width: 1120px; margin: 0 auto; padding: 96px 64px; text-align: left; }
.section h2 { margin: 0 0 8px; font-size: 28px; letter-spacing: -0.04em; }
.note, .section > p { margin: 0 0 16px; color: #44474e; }
.hits { list-style: none; margin: 0; padding: 0; display: grid; gap: 18px; }
.hit a { color: var(--accent); font-size: 20px; text-decoration: none; }
.hit a:hover { text-decoration: underline; }
.hit .site { color: #137333; font-size: 13px; margin-top: 2px; }
.hit p { margin: 4px 0 0; color: #3c4043; }
.facts { display: grid; gap: 12px; }
.facts article { background: #f8f9fa; border-radius: 16px; padding: 16px 18px; }
.facts h3 { margin: 0 0 6px; font-size: 16px; }
.facts p { margin: 0; color: #3c4043; }
.foot { display: flex; justify-content: space-between; gap: 12px; max-width: 760px; margin: 0 auto; padding: 12px 20px 36px; color: #5f6368; }
@media (max-width: 720px) {
  .topbar { flex-wrap: wrap; padding: 16px; }
  .query { border-radius: 18px; flex-direction: column; }
}
`;
}

function searchData(source) {
  return {
    brand: source.brand,
    nav: source.nav,
    hero: source.hero,
    results: source.results,
    sources: source.sources,
    privacy: source.privacy,
  };
}

function searchJs(source) {
  return `// search.json is this same index, written from system.json.
const source = ${JSON.stringify(searchData(source))};
document.querySelectorAll('[data-brand]').forEach((node) => { node.textContent = source.brand; });
const heroTitle = document.querySelector('[data-hero-title]');
const heroLead = document.querySelector('[data-hero-lead]');
if (heroTitle) heroTitle.textContent = source.hero.title;
if (heroLead) heroLead.textContent = source.hero.lead;
const list = document.getElementById('hits');
const note = document.getElementById('query-note');
function draw(items, query) {
  if (!list) return;
  list.replaceChildren();
  items.forEach((item) => {
    const row = document.createElement('li');
    row.className = 'hit';
    const link = document.createElement('a');
    link.href = item.url;
    link.textContent = item.title;
    const site = document.createElement('div');
    site.className = 'site';
    site.textContent = item.site;
    const snippet = document.createElement('p');
    snippet.textContent = item.snippet;
    row.append(link, site, snippet);
    list.appendChild(row);
  });
  if (note) {
    const q = String(query || '').trim();
    note.textContent = q
      ? (items.length ? ('Results for ' + q + '.') : ('No pages matched ' + q + '.'))
      : 'Example results. Type in the search box to narrow them.';
  }
}
function match(query) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return source.results;
  return source.results.filter((item) => (item.title + ' ' + item.site + ' ' + item.snippet).toLowerCase().indexOf(q) !== -1);
}
draw(source.results, '');
const form = document.getElementById('search-form');
const input = document.getElementById('q');
if (form) {
  form.onsubmit = (event) => {
    event.preventDefault();
    const query = input ? input.value : '';
    draw(match(query), query);
    const results = document.getElementById('results');
    if (results && results.scrollIntoView) results.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
}
const privacy = document.querySelector('[data-privacy]');
if (privacy && source.privacy) privacy.textContent = source.privacy.body;
document.querySelectorAll('[data-source]').forEach((node, index) => {
  const item = source.sources[index];
  if (!item) return;
  const title = node.querySelector('h3');
  const body = node.querySelector('p');
  if (title) title.textContent = item.title;
  if (body) body.textContent = item.body;
});
`;
}

function searchHtml(source) {
  const brand = escapeHtml(source.brand);
  const hrefFor = (item) => {
    const key = String(item || '').toLowerCase();
    if (key === 'results') return '#results';
    if (key === 'sources') return '#sources';
    if (key === 'privacy') return '#privacy';
    return '#search';
  };
  const links = (source.nav || []).map((item) => `<a href="${hrefFor(item)}">${escapeHtml(item)}</a>`).join('');
  const hits = (source.results || []).map((item) => `<li class="hit"><a href="${escapeHtml(item.url)}">${escapeHtml(item.title)}</a><div class="site">${escapeHtml(item.site)}</div><p>${escapeHtml(item.snippet)}</p></li>`).join('');
  const facts = (source.sources || []).map((item) => `<article data-source><h3>${escapeHtml(item.title)}</h3><p>${escapeHtml(item.body)}</p></article>`).join('');
  return documentShell(source, `<header class="topbar">
  <a class="logo" href="#search" data-brand>${brand}</a>
  <nav class="nav">${links}</nav>
</header>
<section class="home" id="search">
  <h1 data-hero-title>${escapeHtml(source.hero.title)}</h1>
  <p class="lead" data-hero-lead>${escapeHtml(source.hero.lead)}</p>
  <form class="query" id="search-form" action="#results">
    <label class="sr" for="q" style="position:absolute;left:-9999px">Search</label>
    <input id="q" name="q" type="search" placeholder="Search the web" autocomplete="off" aria-label="Search" />
    <button type="submit">${escapeHtml(source.hero.cta || 'Search')}</button>
  </form>
</section>
<section class="section" id="results">
  <h2>Results</h2>
  <p class="note" id="query-note">Example results. Type in the search box to narrow them.</p>
  <ol class="hits" id="hits">${hits}</ol>
</section>
<section class="section" id="sources">
  <h2>Sources</h2>
  <p>Where a result comes from, and what the line under the title is.</p>
  <div class="facts">${facts}</div>
</section>
<section class="section" id="privacy">
  <h2>${escapeHtml(source.privacy.title)}</h2>
  <p data-privacy>${escapeHtml(source.privacy.body)}</p>
</section>
<footer class="foot"><span>Â© ${brand}</span><span>Search Â· Results Â· Privacy</span></footer>`);
}

function jobCss(source) {
  return `@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;520;650&display=swap');
:root { --accent: ${source.accent}; --ink: ${source.ink}; --header: ${source.header}; }
* { box-sizing: border-box; }
html { scroll-behavior: smooth; }
body { margin: 0; color: var(--ink); background: var(--header); font-family: Outfit, "Segoe UI", sans-serif; }
a { color: inherit; }
button, input, select { font: inherit; }
.topbar { display: flex; align-items: center; gap: 22px; padding: 18px 36px; background: #fff; border-bottom: 1px solid #e4ebe6; }
.logo { font-weight: 650; font-size: 22px; letter-spacing: -0.04em; color: var(--accent); text-decoration: none; }
.nav { display: flex; gap: 18px; margin-right: auto; }
.nav a { text-decoration: none; font-weight: 520; color: #3d4a44; }
.cta { display: inline-flex; align-items: center; justify-content: center; background: var(--accent); color: #f4f7f5; text-decoration: none; border: 0; border-radius: 999px; padding: 12px 18px; font-weight: 650; cursor: pointer; }
.hero { min-height: 100vh; padding: 96px 64px; display: flex; flex-direction: column; justify-content: center; }
.hero h1 { margin: 0; font-size: clamp(64px, 7vw, 80px); letter-spacing: -0.05em; line-height: .98; }
.lead { max-width: 560px; color: #3d4a44; font-size: 18px; }
.section { padding: 96px 64px; }
.section h2 { margin: 0 0 8px; font-size: 32px; letter-spacing: -0.04em; }
.section > p { margin: 0 0 16px; max-width: 560px; color: #3d4a44; }
.list { display: grid; gap: 12px; }
.role, .company { background: #fff; border-radius: 16px; padding: 18px; }
.role h3, .company h3 { margin: 0 0 4px; font-size: 18px; }
.meta { margin: 0 0 8px; color: var(--accent); font-weight: 650; font-size: 14px; }
.role p, .company p { margin: 0; color: #3d4a44; }
.apply { margin: 12px 36px 28px; padding: 24px; border-radius: 24px; background: var(--accent); color: #f4f7f5; display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
.apply h2 { margin: 0 0 8px; font-size: 32px; letter-spacing: -0.04em; }
.apply p { margin: 0; color: rgba(244,247,245,.82); }
form { display: grid; gap: 10px; }
input, select { border: 0; border-radius: 12px; padding: 14px 16px; background: #fff; color: var(--ink); }
form .cta { background: #d7f56a; color: #14221c; }
.done { min-height: 1.2em; font-weight: 650; }
.foot { display: flex; justify-content: space-between; padding: 8px 36px 28px; color: #5c6b64; }
@media (max-width: 800px) {
  .apply, .topbar { grid-template-columns: 1fr; flex-wrap: wrap; }
  .topbar, .hero, .section, .foot { padding-left: 16px; padding-right: 16px; }
  .apply { margin-left: 16px; margin-right: 16px; }
}
`;
}

function jobData(source) {
  return {
    brand: source.brand,
    nav: source.nav,
    hero: source.hero,
    jobs: source.jobs,
    companies: source.companies,
    apply: source.apply,
  };
}

function jobJs(source) {
  return `// jobs.json is this same board, written from system.json.
const source = ${JSON.stringify(jobData(source))};
document.querySelectorAll('[data-brand]').forEach((node) => { node.textContent = source.brand; });
const heroTitle = document.querySelector('[data-hero-title]');
const heroLead = document.querySelector('[data-hero-lead]');
if (heroTitle) heroTitle.textContent = source.hero.title;
if (heroLead) heroLead.textContent = source.hero.lead;
document.querySelectorAll('[data-cta]').forEach((node) => { node.textContent = source.hero.cta; });
document.querySelectorAll('[data-job]').forEach((node, index) => {
  const item = source.jobs[index];
  if (!item) return;
  const title = node.querySelector('h3');
  const meta = node.querySelector('.meta');
  const body = node.querySelector('p');
  if (title) title.textContent = item.title;
  if (meta) meta.textContent = item.company + ' Â· ' + item.place + ' Â· ' + item.type + ' Â· ' + item.pay;
  if (body) body.textContent = item.body;
});
document.querySelectorAll('[data-company]').forEach((node, index) => {
  const item = source.companies[index];
  if (!item) return;
  const title = node.querySelector('h3');
  const meta = node.querySelector('.meta');
  const body = node.querySelector('p');
  if (title) title.textContent = item.title;
  if (meta) meta.textContent = item.roles;
  if (body) body.textContent = item.body;
});
const form = document.getElementById('apply-form');
const done = document.getElementById('applied');
const role = document.getElementById('role');
if (role) {
  source.jobs.forEach((item) => {
    const option = document.createElement('option');
    option.value = item.title;
    option.textContent = item.title + ' at ' + item.company;
    role.appendChild(option);
  });
}
if (form) {
  form.onsubmit = (event) => {
    event.preventDefault();
    const person = (document.getElementById('person') || {}).value || '';
    if (!String(person).trim()) return;
    if (done) {
      done.hidden = false;
      done.textContent = source.apply.done;
    }
    form.reset();
  };
}
`;
}

function jobHtml(source) {
  const brand = escapeHtml(source.brand);
  const hrefFor = (item) => {
    const key = String(item || '').toLowerCase();
    if (key === 'companies') return '#companies';
    if (key === 'apply') return '#apply';
    return '#jobs';
  };
  const links = (source.nav || []).map((item) => `<a href="${hrefFor(item)}">${escapeHtml(item)}</a>`).join('');
  const jobs = (source.jobs || []).map((item) => `<article class="role" data-job><h3>${escapeHtml(item.title)}</h3><p class="meta">${escapeHtml(item.company)} Â· ${escapeHtml(item.place)} Â· ${escapeHtml(item.type)} Â· ${escapeHtml(item.pay)}</p><p>${escapeHtml(item.body)}</p></article>`).join('');
  const companies = (source.companies || []).map((item) => `<article class="company" data-company><h3>${escapeHtml(item.title)}</h3><p class="meta">${escapeHtml(item.roles)}</p><p>${escapeHtml(item.body)}</p></article>`).join('');
  return documentShell(source, `<header class="topbar">
  <a class="logo" href="#jobs" data-brand>${brand}</a>
  <nav class="nav">${links}</nav>
  <a class="cta" href="#apply" data-cta>${escapeHtml(source.hero.cta)}</a>
</header>
<section class="hero" id="top">
  <h1 data-hero-title>${escapeHtml(source.hero.title)}</h1>
  <p class="lead" data-hero-lead>${escapeHtml(source.hero.lead)}</p>
</section>
<section class="section" id="jobs">
  <h2>Jobs</h2>
  <p>Open roles you can apply for from this page.</p>
  <div class="list">${jobs}</div>
</section>
<section class="section" id="companies">
  <h2>Companies</h2>
  <p>Who is hiring, and how they read an application.</p>
  <div class="list">${companies}</div>
</section>
<section class="apply" id="apply">
  <div>
    <h2>${escapeHtml(source.apply.title)}</h2>
    <p>${escapeHtml(source.apply.lead)}</p>
  </div>
  <form id="apply-form">
    <input id="person" name="name" placeholder="Your name" autocomplete="name" />
    <input id="email" name="email" type="email" placeholder="Email" autocomplete="email" />
    <select id="role" name="role" aria-label="Role"><option value="">Choose a role</option></select>
    <button class="cta" type="submit">${escapeHtml(source.apply.cta)}</button>
    <p class="done" id="applied" hidden></p>
  </form>
</section>
<footer class="foot"><span>Â© ${brand}</span><span>Jobs Â· Companies Â· Apply</span></footer>`);
}

function studiedCss(source) {
  const dark = Boolean(source.dark);
  const card = dark ? '#1c1f27' : '#fff';
  const muted = dark ? '#c8c2b8' : '#44403c';
  const inkOnAccent = dark ? '#14161c' : '#fff';
  return `@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;520;650&display=swap');
:root { --accent: ${source.accent}; --ink: ${source.ink}; --header: ${source.header}; --card: ${card}; --muted: ${muted}; }
* { box-sizing: border-box; }
html, body { margin: 0; min-height: 100%; }
html { scroll-behavior: smooth; }
body { color: var(--ink); background: var(--header); font-family: Outfit, "Segoe UI", sans-serif; }
a { color: inherit; }
.topbar { position: sticky; top: 0; z-index: 2; display: flex; align-items: center; gap: 28px; padding: 22px 64px; background: var(--header); }
.logo { font-weight: 650; letter-spacing: -0.04em; font-size: 22px; color: var(--accent); }
.nav { display: flex; gap: 22px; margin-left: auto; }
.nav a { text-decoration: none; font-weight: 520; font-size: 15px; }
.hero { min-height: 100vh; display: grid; grid-template-columns: 1.15fr .85fr; gap: 48px; align-items: center; padding: 96px 64px; }
.hero h1 { margin: 0; max-width: 14ch; font-size: clamp(64px, 7vw, 80px); line-height: .92; letter-spacing: -0.055em; }
.lead { max-width: 640px; margin: 24px 0 0; color: var(--muted); font-size: 20px; line-height: 1.5; }
.cta { display: inline-block; margin-top: 32px; background: var(--accent); color: ${inkOnAccent}; text-decoration: none; border-radius: 999px; padding: 16px 28px; font-weight: 650; font-size: 16px; }
.panel { width: 100%; min-height: 460px; border-radius: 32px; background: linear-gradient(160deg, var(--accent), var(--card)); }
.band, .detail { padding: 96px 64px; }
.band { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 24px; }
.band article { background: var(--card); border-radius: 24px; padding: 32px; min-height: 220px; }
.band h2, .detail h2 { margin: 0 0 12px; font-size: 32px; letter-spacing: -0.04em; }
.band p, .detail p { margin: 0; color: var(--muted); font-size: 18px; line-height: 1.55; }
.detail { display: grid; grid-template-columns: .8fr 1.2fr; gap: 48px; align-items: start; }
.foot { display: flex; justify-content: space-between; gap: 24px; padding: 32px 64px 48px; color: var(--muted); font-size: 14px; }
@media (max-width: 900px) {
  .hero, .detail, .band { grid-template-columns: 1fr; }
  .topbar, .hero, .band, .detail, .foot { padding-left: 24px; padding-right: 24px; }
  .nav { width: 100%; margin-left: 0; flex-wrap: wrap; }
}
`;
}

function studiedData(source) {
  return {
    brand: source.brand,
    nav: source.nav,
    hero: source.hero,
    features: source.features,
    studied: source.studied,
  };
}

function studiedJs(source) {
  return `// studied.json is this same page, written from system.json.
const source = ${JSON.stringify(studiedData(source))};
document.querySelectorAll('[data-brand]').forEach((node) => { node.textContent = source.brand; });
const title = document.querySelector('[data-hero-title]');
const lead = document.querySelector('[data-hero-lead]');
if (title) title.textContent = source.hero.title;
if (lead) lead.textContent = source.hero.lead;
document.querySelectorAll('[data-cta]').forEach((node) => { node.textContent = source.hero.cta; });
document.querySelectorAll('[data-feature]').forEach((node, index) => {
  const feature = source.features[index];
  if (!feature) return;
  const heading = node.querySelector('h2');
  const body = node.querySelector('p');
  if (heading) heading.textContent = feature.title;
  if (body) body.textContent = feature.body;
});
`;
}

function studiedHtml(source) {
  const brand = escapeHtml(source.brand);
  const features = source.features || [];
  const links = (source.nav || []).map((item, index) => {
    const id = features[index]?.id || `section-${index}`;
    return `<a href="#${escapeHtml(id)}">${escapeHtml(item)}</a>`;
  }).join('');
  const cards = features.map((feature, index) => `<article id="${escapeHtml(feature.id || `section-${index}`)}" data-feature="${index}"><h2>${escapeHtml(feature.title)}</h2><p>${escapeHtml(feature.body)}</p></article>`).join('');
  const detail = features[features.length - 1] || { title: brand, body: source.hero?.lead || '' };
  return documentShell(source, `<header class="topbar">
  <div class="logo" data-brand>${brand}</div>
  <nav class="nav">${links}</nav>
</header>
<section class="hero">
  <div>
    <h1 data-hero-title>${escapeHtml(source.hero.title)}</h1>
    <p class="lead" data-hero-lead>${escapeHtml(source.hero.lead)}</p>
    <a class="cta" href="#${escapeHtml(features[0]?.id || 'section-0')}" data-cta>${escapeHtml(source.hero.cta)}</a>
  </div>
  <div class="panel" aria-hidden="true"></div>
</section>
<section class="band">${cards}</section>
<section class="detail" id="${escapeHtml(detail.id || 'detail')}">
  <h2>${escapeHtml(detail.title)}</h2>
  <p>${escapeHtml(detail.body || source.hero?.lead || '')}</p>
</section>
<footer class="foot"><span>Â© ${brand}</span><span>${escapeHtml((source.nav || []).slice(0, 4).join(' Â· '))}</span></footer>`);
}

function joinList(items) {
  const list = items.filter(Boolean);
  if (list.length <= 1) return list[0] || '';
  if (list.length === 2) return `${list[0]} and ${list[1]}`;
  return `${list.slice(0, -1).join(', ')}, and ${list[list.length - 1]}`;
}

function readme(source) {
  const name = siteName(source);
  const summary = siteSummary(source);
  const accent = source.accent;
  const slug = siteSlug(source);
  if (source.kind === 'stays') {
    const places = joinList((source.listings || []).slice(0, 4).map((item) => item.place));
    const categories = joinList((source.categories || []).filter((item) => item && item !== 'All'));
    return `# ${name}

${summary}

The accent color is ${accent}. The name, that color, and the homes are written from system.json, so this readme, the page, and listings.json stay on the same facts.

Categories include ${categories}. The first places in the grid are ${places}.

## Run it locally

This folder is a static site. Install the packages, then start the server from this folder.

\`\`\`
npm install
npm start
\`\`\`

The start script runs serve, which is the only package this site needs. It opens this folder in the browser. index.html is the page, styles.css is the layout, and app.js is the search.

## Files

- system.json is the source for the name, the colors, the categories, and the homes.
- site.config.json is the short config: the name, the accent ${accent}, and the description.
- listings.json is the same homes the grid renders.
- index.html is the search bar, the category row, and the grid.
- styles.css paints the logo and the search button in ${accent}.
- app.js filters by place and category, and saves a heart on a card.
- package.json names the project ${slug} and starts the static server.
`;
  }
  if (source.kind === 'bank') {
    const accounts = joinList((source.accounts || []).map((item) => item.title));
    const fees = joinList((source.fees || []).map((item) => `${item.title} ${item.price}`));
    const nav = joinList(source.nav || []);
    return `# ${name}

${summary}

The accent color is ${accent}. The name, the accounts, the fees, and the button are written from system.json, so this readme and the page stay on the same facts.

The navigation reads ${nav}. The accounts are ${accounts}. The fees are ${fees}. The main action is ${source.hero?.cta || 'Open an account'}.

## Run it locally

This folder is a static site. Install the packages, then start the server from this folder.

\`\`\`
npm install
npm start
\`\`\`

The start script runs serve, which is the only package this site needs. It opens this folder in the browser. index.html is the page, styles.css is the layout, and app.js fills the accounts, cards, transfers, security, and fees.

## Files

- system.json is the source for the name, the colors, the accounts, and the fees.
- site.config.json is the short config: the name, the accent ${accent}, and the description.
- bank.json is the same accounts, cards, transfers, security notes, and fees the page shows.
- index.html is the hero, those sections, and the form to open an account.
- styles.css paints the logo, the card, and the button in ${accent}.
- app.js writes each section from the same source and starts an application.
- package.json names the project ${slug} and starts the static server.
`;
  }
  if (source.kind === 'search') {
    const nav = joinList(source.nav || []);
    const sample = joinList((source.results || []).slice(0, 3).map((item) => item.title));
    return `# ${name}

${summary}

The accent color is ${accent}. The name, the example results, and the search box are written from system.json, so this readme and the page stay on the same facts.

The navigation reads ${nav}. The first results are ${sample}. The main action is the search box.

## Run it locally

This folder is a static site. Install the packages, then start the server from this folder.

\`\`\`
npm install
npm start
\`\`\`

The start script runs serve, which is the only package this site needs. It opens this folder in the browser. index.html is the page, styles.css is the layout, and app.js filters the results from the search box.

## Files

- system.json is the source for the name, the colors, and the results.
- site.config.json is the short config: the name, the accent ${accent}, and the description.
- search.json is the same results, sources, and privacy note the page shows.
- index.html is the search box, the result rows, the sources, and the privacy note.
- styles.css paints the logo and the search button in ${accent}.
- app.js filters the result rows from the words in the search box.
- package.json names the project ${slug} and starts the static server.
`;
  }
  if (source.kind === 'jobs') {
    const nav = joinList(source.nav || []);
    const roles = joinList((source.jobs || []).map((item) => item.title));
    return `# ${name}

${summary}

The accent color is ${accent}. The name, the roles, the companies, and the application are written from system.json, so this readme and the page stay on the same facts.

The navigation reads ${nav}. The roles are ${roles}. The main action is ${source.hero?.cta || 'Apply for a role'}.

## Run it locally

This folder is a static site. Install the packages, then start the server from this folder.

\`\`\`
npm install
npm start
\`\`\`

The start script runs serve. index.html is the page, styles.css is the layout, and app.js fills the roles, the companies, and the application.

## Files

- system.json is the source for the name, the roles, and the companies.
- site.config.json is the short config: the name, the accent ${accent}, and the description.
- jobs.json is the same roles, companies, and application the page shows.
- index.html is the roles, the companies, and the application form.
- styles.css paints the logo and the button in ${accent}.
- app.js writes each role from the same source and sends an application.
- package.json names the project ${slug} and starts the static server.
`;
  }
  if (source.kind === 'studied') {
    const nav = joinList(source.nav || []);
    return `# ${name}

${summary}

The accent color is ${accent}. The name, the sections, and the button are written from system.json, so this readme and the page stay on the same facts.

The navigation reads ${nav}. The public page that was checked is ${source.studied?.url || 'recorded in system.json'}. The pictures on that page are not copied here.

## Run it locally

This folder is a static site. Install the packages, then start the server from this folder.

\`\`\`
npm install
npm start
\`\`\`

## Files

- system.json is the source for the name, the colors, and the sections.
- studied.json is the same headline and sections the page shows.
- index.html is the hero and the sections.
- styles.css paints the page from the colors found on the public site.
- app.js writes the headline and the sections from the same source.
- package.json names the project ${slug} and starts the static server.
`;
  }
  const points = joinList((source.features || []).map((item) => item.title));
  const nav = joinList(source.nav || []);
  return `# ${name}

${summary}

The accent color is ${accent}. The name, that color, and the points on the page are written from system.json, so this readme and the page stay on the same facts.

The navigation reads ${nav}. The points are ${points}.

## Run it locally

This folder is a static site. Install the packages, then start the server from this folder.

\`\`\`
npm install
npm start
\`\`\`

The start script runs serve, which is the only package this site needs. It opens this folder in the browser. index.html is the page, styles.css is the layout, and app.js fills the three points.

## Files

- system.json is the source for the name, the colors, the headline, and the points.
- site.config.json is the short config: the name, the accent ${accent}, and the description.
- features.json is the same headline and points the page shows.
- index.html is the headline, the picture, and the three cards.
- styles.css paints the logo and the button in ${accent}.
- app.js writes each point into its card.
- package.json names the project ${slug} and starts the static server.
`;
}

function packageJson(source) {
  return `${JSON.stringify({
    name: siteSlug(source),
    version: '1.0.0',
    private: true,
    description: siteSummary(source),
    scripts: {
      start: 'serve .',
      dev: 'serve .',
    },
    devDependencies: {
      serve: '^14.2.4',
    },
  }, null, 2)}\n`;
}

function gitignore(source) {
  return `# ${siteName(source)} local files
node_modules/
dist/
.env
.env.*
.DS_Store
npm-debug.log*
`;
}

function siteConfig(source) {
  const config = {
    name: siteName(source),
    slug: siteSlug(source),
    kind: source.kind,
    title: pageTitle(source),
    description: siteSummary(source),
    accent: source.accent,
    ink: source.ink,
    header: source.header,
  };
  if (source.kind === 'stays') config.categories = source.categories;
  else config.nav = source.nav;
  return `${JSON.stringify(config, null, 2)}\n`;
}

function siteDataFile(source) {
  if (source.kind === 'stays') {
    return {
      path: 'listings.json',
      language: 'json',
      content: `${JSON.stringify(staysData(source), null, 2)}\n`,
    };
  }
  if (source.kind === 'bank') {
    return {
      path: 'bank.json',
      language: 'json',
      content: `${JSON.stringify(bankData(source), null, 2)}\n`,
    };
  }
  if (source.kind === 'search') {
    return {
      path: 'search.json',
      language: 'json',
      content: `${JSON.stringify(searchData(source), null, 2)}\n`,
    };
  }
  if (source.kind === 'jobs') {
    return {
      path: 'jobs.json',
      language: 'json',
      content: `${JSON.stringify(jobData(source), null, 2)}\n`,
    };
  }
  if (source.kind === 'store') {
    return {
      path: 'catalog.json',
      language: 'json',
      content: `${JSON.stringify(storeData(source), null, 2)}\n`,
    };
  }
  if (source.kind === 'studied') {
    return {
      path: 'studied.json',
      language: 'json',
      content: `${JSON.stringify(studiedData(source), null, 2)}\n`,
    };
  }
  return {
    path: 'features.json',
    language: 'json',
    content: `${JSON.stringify(productData(source), null, 2)}\n`,
  };
}

function storeSource(brand, audience) {
  const who = audience || 'people using this page';
  return {
    kind: 'store',
    brand,
    accent: '#ff9900',
    ink: '#0f1111',
    header: '#131921',
    nav: ['Today', 'Deals', 'Electronics', 'Home', 'Fashion'],
    hero: {
      title: `Find it on ${brand}.`,
      lead: `${brand} is a shop for ${who}. Search for an item, see the price, and add it from this page.`,
      cta: 'Search',
    },
    departments: ['All', 'Deals', 'Electronics', 'Home', 'Fashion', 'Books'],
    products: [
      { id: 'lamp', title: 'Brass desk lamp', dept: 'Home', price: 'Â£42', note: 'In stock. Leaves the warehouse today.', deal: false },
      { id: 'headphones', title: 'Wireless headphones', dept: 'Electronics', price: 'Â£79', note: 'In stock. A spare cable is in the box.', deal: true },
      { id: 'kettle', title: 'Steel kettle', dept: 'Home', price: 'Â£28', note: 'In stock. Boils a full litre.', deal: false },
      { id: 'coat', title: 'Wool coat', dept: 'Fashion', price: 'Â£120', note: 'Two sizes left. Ships in a plain box.', deal: true },
      { id: 'novel', title: 'Paperback novel', dept: 'Books', price: 'Â£9', note: 'In stock. The next print run is dated inside.', deal: false },
      { id: 'speaker', title: 'Small speaker', dept: 'Electronics', price: 'Â£35', note: 'In stock. Charges from a cable.', deal: true },
    ],
  };
}

function storeData(source) {
  return { brand: source.brand, departments: source.departments, products: source.products };
}

function storeCss(source) {
  return `* { box-sizing: border-box; }
html, body { margin: 0; min-height: 100%; background: #e3e6e6; color: ${source.ink}; font-family: Arial, Helvetica, sans-serif; }
.top { background: ${source.header}; color: #fff; padding: 12px 28px; display: flex; align-items: center; gap: 16px; }
.mark { width: 40px; height: 40px; border-radius: 8px; background: ${source.accent}; color: #111; font-weight: 700; display: grid; place-items: center; font-size: 20px; flex: none; }
.word { font-size: 22px; font-weight: 700; letter-spacing: -0.03em; }
.search { flex: 1; display: flex; min-width: 0; }
.search input { flex: 1; border: 0; border-radius: 8px 0 0 8px; padding: 12px 14px; font-size: 16px; }
.search button { border: 0; background: ${source.accent}; color: #111; font-weight: 700; padding: 0 18px; border-radius: 0 8px 8px 0; cursor: pointer; }
.depts { background: #232f3e; color: #fff; display: flex; gap: 8px; padding: 8px 28px; overflow: auto; }
.depts button { background: transparent; color: #fff; border: 0; padding: 8px 10px; cursor: pointer; font-size: 14px; }
.depts button.on { background: #37475a; border-radius: 6px; }
.banner { margin: 18px 28px; background: linear-gradient(100deg, #1b3a4b, #2a6f7f); color: #fff; border-radius: 8px; min-height: 180px; padding: 36px 40px; }
.banner h1 { margin: 0 0 8px; font-size: clamp(32px, 4vw, 48px); }
.banner p { margin: 0; max-width: 36rem; font-size: 18px; line-height: 1.45; }
.grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 14px; padding: 0 28px 40px; }
.card { background: #fff; border-radius: 8px; padding: 16px; min-height: 160px; display: flex; flex-direction: column; gap: 8px; }
.card strong { font-size: 18px; }
.price { font-size: 20px; }
.card button { margin-top: auto; align-self: flex-start; background: ${source.accent}; border: 0; border-radius: 16px; padding: 8px 14px; cursor: pointer; font-weight: 700; }
.foot { background: ${source.header}; color: #ddd; text-align: center; padding: 22px; }
@media (max-width: 800px) { .grid { grid-template-columns: 1fr; } .top { flex-wrap: wrap; } }
`;
}

function storeJs() {
  return `const data = window.SITE;
const grid = document.querySelector('#grid');
const input = document.querySelector('#q');
const buttons = [...document.querySelectorAll('[data-dept]')];
let dept = 'All';
function draw() {
  const q = input.value.trim().toLowerCase();
  const items = data.products.filter((item) => (dept === 'All' || item.dept === dept || (dept === 'Deals' && item.deal)) && (!q || item.title.toLowerCase().includes(q)));
  grid.innerHTML = items.map((item) => '<article class="card"><strong>' + item.title + '</strong><div class="price">' + item.price + '</div><p>' + item.note + '</p><button type="button">Add</button></article>').join('') || '<p>Nothing matches that search.</p>';
}
buttons.forEach((button) => button.addEventListener('click', () => {
  dept = button.getAttribute('data-dept');
  buttons.forEach((item) => item.classList.toggle('on', item === button));
  draw();
}));
input.addEventListener('input', draw);
document.querySelector('#find').addEventListener('click', draw);
draw();
`;
}

function storeHtml(source) {
  const mark = String(source.brand || 'S').trim().charAt(0).toUpperCase();
  const depts = (source.departments || []).map((name) => `<button type="button" data-dept="${name}"${name === 'All' ? ' class="on"' : ''}>${name}</button>`).join('');
  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>${source.brand}</title>
  <link rel="stylesheet" href="styles.css" />
</head>
<body>
  <header class="top">
    <div class="mark" aria-hidden="true">${mark}</div>
    <div class="word">${source.brand}</div>
    <form class="search" action="#" onsubmit="return false">
      <input id="q" type="search" placeholder="Search ${source.brand}" aria-label="Search" />
      <button id="find" type="submit">Search</button>
    </form>
  </header>
  <nav class="depts">${depts}</nav>
  <section class="banner">
    <h1>${source.hero.title}</h1>
    <p>${source.hero.lead}</p>
  </section>
  <main class="grid" id="grid"></main>
  <footer class="foot">Â© ${source.brand}</footer>
  <script>window.SITE = ${JSON.stringify(storeData(source))};</script>
  <script src="app.js"></script>
</body>
</html>
`;
}

function pageParts(source) {
  if (source.kind === 'stays') return { css: staysCss(source), js: staysJs(source), html: staysHtml(source) };
  if (source.kind === 'bank') return { css: bankCss(source), js: bankJs(source), html: bankHtml(source) };
  if (source.kind === 'search') return { css: searchCss(source), js: searchJs(source), html: searchHtml(source) };
  if (source.kind === 'jobs') return { css: jobCss(source), js: jobJs(source), html: jobHtml(source) };
  if (source.kind === 'store') return { css: storeCss(source), js: storeJs(source), html: storeHtml(source) };
  if (source.kind === 'studied') return { css: studiedCss(source), js: studiedJs(source), html: studiedHtml(source) };
  return { css: productCss(source), js: productJs(source), html: productHtml(source) };
}

export function filesFromSource(source) {
  if (source?.kind === 'slides') return deckFiles(source);
  if (source?.kind === 'rendered') {
    const meta = {
      kind: 'rendered',
      brand: source.brand || 'Site',
      brief: source.brief || '',
      generatedBy: 'model',
    };
    return [
      { path: 'system.json', language: 'json', content: `${JSON.stringify(meta, null, 2)}\n` },
      { path: 'README.md', language: 'markdown', content: `# ${meta.brand}\n\nGenerated by Zolaid from your brief.\n` },
      { path: 'styles.css', language: 'css', content: String(source.css || '') },
      { path: 'app.js', language: 'javascript', content: String(source.js || '') },
      { path: 'index.html', language: 'html', content: String(source.html || '') },
    ];
  }
  const page = pageParts(source);
  return [
    { path: 'system.json', language: 'json', content: `${JSON.stringify(source, null, 2)}\n` },
    { path: 'README.md', language: 'markdown', content: readme(source) },
    { path: 'package.json', language: 'json', content: packageJson(source) },
    { path: '.gitignore', language: 'gitignore', content: gitignore(source) },
    { path: 'site.config.json', language: 'json', content: siteConfig(source) },
    siteDataFile(source),
    { path: 'styles.css', language: 'css', content: page.css },
    { path: 'app.js', language: 'javascript', content: page.js },
    { path: 'index.html', language: 'html', content: page.html },
  ];
}

export const SITE_SUBJECT_QUESTION = 'What is the site for?';
export const SITE_ACTION_QUESTION = 'What should a visitor do first?';
export const SITE_AUDIENCE_QUESTION = 'Who is it for?';
export const COMPUTER_LINE = 'Building it on the computer.';
const MAX_SITE_QUESTIONS = 4;

function blankBrief() {
  return { subject: '', action: '', audience: '', note: '', asking: '', asked: 0 };
}

function clipPhrase(value) {
  return String(value || '')
    .replace(/\b(website|web site|site|page|homepage|landing page)\b/gi, ' ')
    .replace(/[.?!]+$/g, '')
    .split(/\b(?:they|who|that|which|and then)\b/i)[0]
    .replace(/\s+/g, ' ')
    .trim();
}

function looksLikeAudience(phrase) {
  return /\b(people|customers|clients|users|visitors|students|parents|kids|locals|neighbors|everyone|anyone|folks|teams|companies|businesses|founders|travelers|nearby)\b/i.test(String(phrase || ''));
}

function looksLikeSubject(phrase) {
  const p = String(phrase || '');
  if (!p || looksLikeAudience(p)) return false;
  if (knownKind(p)) return true;
  return /\b(app|product|brand|service|business|company|portfolio|agency|salon|gym|studio|hotel|bar|pub|blog|newsletter|restaurant|cafe|caf[eé]|bakery|coffee shop|diner|bistro|school|course|clinic|shop|store)\b/i.test(p);
}

function siteSubject(text) {
  const raw = correctKnownNameTypos(String(text || ''));
  const named = referenceQuery(raw) || namedProductQuery(raw);
  if (named && !/^me$/i.test(named) && !isChatFollowUp(named) && !looksLikePastedBrief(named)) {
    return clampWords(titleName(named), 4, 32);
  }
  const forMatch = raw.match(/\b(?:for|about)\s+(?:a|an|my|our|the)\s+([a-z][\w\s'-]{1,40})/i)
    || raw.match(/\b(?:for|about)\s+([a-z][\w\s'-]{1,40})/i);
  if (forMatch) {
    const phrase = clipPhrase(forMatch[1]).replace(/\b(with|that|which|including|includes?).*$/i, ' ').replace(/\s+/g, ' ').trim();
    if (phrase && looksLikeSubject(phrase) && !looksLikePastedBrief(phrase)) {
      return clampWords(titleName(phrase), 4, 32);
    }
  }
  const category = categoryBrand(raw);
  if (category) return category;
  return '';
}

function knownKind(text) {
  const raw = String(text || '');
  if (wantsBank(raw)) return 'bank';
  if (wantsSearch(raw) || isSearchName(referenceQuery(raw))) return 'search';
  if (wantsJobs(raw)) return 'jobs';
  if (wantsStays(raw)) return 'stays';
  if (wantsAgent(raw)) return 'agent';
  if (wantsBlog(raw)) return 'blog';
  if (/\b(restaurant|cafe|cafÃ©|caf[eé]|bakery|coffee shop|diner|bistro)\b/i.test(raw)) return 'place';
  if (/\b(school|course|class|lesson|tutor|university)\b/i.test(raw)) return 'place';
  if (/\b(clinic|doctor|dentist|hospital|pharmacy)\b/i.test(raw)) return 'place';
  if (/\b(shop|store|marketplace|e-?commerce|buy|cart)\b/i.test(raw)) return 'shop';
  return '';
}

function visitorNote(text) {
  const match = String(text || '').match(/\b(?:they should|visitors? should|people should|so (?:they|people|visitors?) can)\s+([^.!?]+)/i);
  return match ? match[1].replace(/\s+/g, ' ').trim() : '';
}

function actionIn(text) {
  const kind = knownKind(text);
  if (kind) return kind;
  const t = String(text || '').toLowerCase();
  if (/\b(search|look up|lookup)\b/.test(t)) return 'search';
  if (/\b(shop|store|buy|cart|product|marketplace)\b/.test(t)) return 'shop';
  if (/\b(sign up|signup|subscribe|register|join|account)\b/.test(t)) return 'signup';
  if (/\b(blog|newsletter|read|learn|article|story|posts?)\b/.test(t)) return 'blog';
  if (/\b(menu|book|table|order|find the shop|directions|hours)\b/.test(t)) return 'place';
  if (visitorNote(text)) return 'custom';
  return '';
}

function audienceIn(text) {
  const raw = String(text || '');
  const explicit = raw.match(/\bfor\s+((?:people|customers|clients|users|visitors|students|locals|parents|kids|neighbors|teams|companies|founders|travelers)[\w\s'-]{0,40})/i);
  if (explicit) {
    const phrase = clipPhrase(explicit[1]);
    if (phrase) return phrase;
  }
  const match = raw.match(/\bfor\s+(?!me\b)([a-z][\w\s'-]{1,48})/i);
  if (!match) return '';
  const phrase = clipPhrase(match[1]);
  if (!phrase || (looksLikeSubject(phrase) && !looksLikeAudience(phrase))) return '';
  return phrase;
}

function actionFromAnswer(text) {
  const inferred = actionIn(text);
  if (inferred) return inferred;
  return 'custom';
}

function askSiteTurn(state, field, chat) {
  const asked = Number(state.asked || 0) + 1;
  if (asked > MAX_SITE_QUESTIONS && state.subject) {
    return {
      action: 'build',
      source: sourceFromAnswers({
        ...state,
        action: state.action || 'custom',
        audience: state.audience || 'visitors',
        note: state.note || '',
      }),
      chat: COMPUTER_LINE,
      brief: blankBrief(),
    };
  }
  return { action: 'ask', chat, brief: { ...state, asking: field, asked } };
}

function sourceFromAnswers(state) {
  const brand = clampWords(String(state.subject || shortBrandFromBrief(state.note || '', 'Studio')), 4, 36);
  const who = state.audience;
  const joined = `${brand} ${state.note || ''}`;
  if (state.action === 'search' || wantsSearch(joined)) {
    const source = searchSource(brand);
    source.brand = brand;
    source.hero.lead = `${brand} is for ${who}. Type a question. The result shows the page title, the site it came from, and one line from that page.`;
    return sanitizeSourceCopy(source);
  }
  if (state.action === 'shop') return sanitizeSourceCopy(storeSource(brand, who));
  if (state.action === 'bank' || wantsBank(joined)) {
    const source = bankSource(brand);
    source.brand = brand;
    source.hero.lead = `${brand} holds a current account, savings, and a card for ${who}. A payment shows the fee before the money leaves.`;
    return sanitizeSourceCopy(source);
  }
  if (state.action === 'jobs' || wantsJobs(joined)) {
    const source = jobSource(joined);
    source.brand = brand;
    source.hero.lead = `${brand} lists open roles for ${who}, and an application can be sent from this page.`;
    return sanitizeSourceCopy(source);
  }
  if (state.action === 'stays' || wantsStays(joined)) {
    const source = staysSource(brand);
    source.brand = brand.toLowerCase();
    return sanitizeSourceCopy(source);
  }
  if (state.action === 'agent' || wantsAgent(joined)) {
    const source = agentSource(brand);
    source.brand = brand;
    source.hero.lead = `${brand} takes a goal for ${who}, plans the steps, and stops when it needs a decision.`;
    return sanitizeSourceCopy(source);
  }
  if (state.action === 'blog' || state.action === 'read' || wantsBlog(joined) || wantsBlog(brand)) {
    const source = blogSource(`${brand} personal blog ${state.note || ''}`);
    source.brand = looksLikePastedBrief(brand) ? 'Personal Blog' : brand;
    source.hero.lead = `${source.brand} is for ${who || 'readers'}. Home, about, and blog posts stay on one page.`;
    return sanitizeSourceCopy(source);
  }
  if (state.action === 'place') {
    const offeredPlace = offeringSource(brand);
    if (offeredPlace) {
      offeredPlace.brand = brand;
      offeredPlace.hero.lead = `${offeredPlace.hero.lead} This page is for ${who}.`;
      return sanitizeSourceCopy(offeredPlace);
    }
  }
  const offered = offeringSource(joined);
  if (offered) {
    offered.brand = brand;
    offered.hero.lead = `${offered.hero.lead} This page is for ${who}.`;
    return sanitizeSourceCopy(offered);
  }
  const doing = state.action === 'signup'
    ? 'leave a name and start'
    : (state.note || 'take the first step');
  const theme = themeFromBrief(joined, { accent: '#143d36', ink: '#14221f', header: '#f3f6f4' });
  return sanitizeSourceCopy({
    kind: 'product',
    offer: 'answered',
    brand,
    accent: theme.accent,
    ink: theme.ink,
    header: theme.header,
    nav: ['Welcome', 'What you can do', 'Who it is for'],
    hero: {
      title: clampWords(brand, 6, 48),
      lead: `${brand} is for ${who}. They can ${doing} from the first screen.`,
      cta: state.action === 'signup' ? 'Sign up' : 'Begin',
    },
    features: [
      { id: 'welcome', title: 'Welcome', body: `${brand} opens with one clear next step for ${who}.` },
      { id: 'do', title: 'What you can do', body: `People use this page to ${doing}. The button stays on the screen.` },
      { id: 'who', title: 'Who it is for', body: `Written for ${who}. The rest of the page follows that.` },
    ],
  });
}

function knownBrandToken(text) {
  const tokens = String(text || '').trim().split(/\s+/).filter(Boolean);
  if (tokens.length !== 1) return '';
  return BRAND_KEYS.get(tokens[0].toLowerCase()) || '';
}

function bareSubject(text) {
  const raw = String(text || '').trim();
  if (!raw || isChatFollowUp(raw)) return '';
  if (/^(ok|okay|yes|no|thanks|thank you|hi|hello|hey|me|a|an|the|my|our)$/i.test(raw)) return '';
  if (raw.split(/\s+/).length > 8) return '';
  return raw;
}

function researchTurn(raw) {
  if (isChatFollowUp(raw)) return null;
  if (skipsReferenceLookup(raw)) return null;
  const ref = referenceQuery(raw) || namedProductQuery(raw);
  if (!ref || isChatFollowUp(ref)) return null;
  return { action: 'research', query: ref, chat: COMPUTER_LINE };
}

export function nextSiteTurn(text, brief = null) {
  const raw = correctKnownNameTypos(String(text || '').trim());
  if (!raw) return { action: 'pass' };
  if (isChatFollowUp(raw)) return { action: 'explain' };
  if (isDeckRequest(raw)) return { action: 'pass' };
  const state = brief && typeof brief === 'object' ? { ...blankBrief(), ...brief } : blankBrief();
  if (brief === true) state.asking = state.asking || 'subject';

  if (isWebsiteRequest(raw)) {
    state.subject = siteSubject(raw);
    state.action = actionIn(raw);
    state.audience = audienceIn(raw);
    state.note = visitorNote(raw);
    state.asking = '';
    state.asked = 0;
  } else if (state.asking === 'subject') {
    const subject = siteSubject(raw) || titleName(bareSubject(raw));
    if (!subject) return askSiteTurn(state, 'subject', SITE_SUBJECT_QUESTION);
    state.subject = subject;
    state.action = actionIn(raw) || state.action;
    state.audience = audienceIn(raw) || state.audience;
    state.note = visitorNote(raw) || state.note;
    state.asking = '';
  } else if (state.asking === 'action') {
    state.action = actionFromAnswer(raw);
    state.note = raw.replace(/\s+/g, ' ').trim();
    state.audience = audienceIn(raw) || state.audience;
    state.asking = '';
  } else if (state.asking === 'audience') {
    const audience = raw.replace(/[.?!]+$/g, '').replace(/\s+/g, ' ').trim();
    if (!audience) return askSiteTurn(state, 'audience', SITE_AUDIENCE_QUESTION);
    state.audience = audience;
    if (!state.action) state.action = actionIn(raw);
    state.note = visitorNote(raw) || state.note;
    state.asking = '';
  } else {
    return { action: 'pass' };
  }

  if (!state.subject) return askSiteTurn(state, 'subject', SITE_SUBJECT_QUESTION);
  if (Number(state.asked || 0) >= MAX_SITE_QUESTIONS) {
    return {
      action: 'build',
      source: sourceFromAnswers({
        ...state,
        action: state.action || 'custom',
        audience: state.audience || 'visitors',
      }),
      chat: COMPUTER_LINE,
      brief: blankBrief(),
    };
  }
  if (!state.action) return askSiteTurn(state, 'action', SITE_ACTION_QUESTION);
  if (!state.audience) return askSiteTurn(state, 'audience', SITE_AUDIENCE_QUESTION);
  return { action: 'build', source: sourceFromAnswers(state), chat: COMPUTER_LINE, brief: blankBrief() };
}

function researchSentences(about) {
  return String(about || '')
    .split(/(?<=[.!?])\s+/)
    .map((line) => line.replace(/\s+/g, ' ').trim())
    .filter((line) => line.length > 24 && line.length < 320);
}

function sectionSlug(title, index) {
  const slug = String(title || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  return slug || `section-${index}`;
}

function researchTitles(research) {
  const seen = new Set();
  const titles = [];
  const blocked = /^(the work|on this page|open studio|studio|menu|skip|home)$/i;
  for (const item of [...(research?.nav || []), ...(research?.headings || [])]) {
    const title = String(item || '').replace(/\s+/g, ' ').trim();
    const key = title.toLowerCase();
    if (!title || title.length > 48 || blocked.test(title) || seen.has(key)) continue;
    seen.add(key);
    titles.push(title);
    if (titles.length >= 6) break;
  }
  return titles;
}

export function sourceFromResearch(brief, research) {
  const about = String(research?.about || '').replace(/\s+/g, ' ').trim();
  const titles = researchTitles(research);
  const sentences = researchSentences(about);
  if (!titles.length && about.length < 40) return null;
  const corrected = correctKnownNameTypos(String(brief || ''));
  const explicit = explicitBrand(brief);
  const rawQuery = String(research?.query || '').trim();
  const queryName = (!explicit && /^me$/i.test(rawQuery))
    ? ''
    : titleName(rawQuery || referenceQuery(corrected) || '');
  const subject = briefSubject(corrected).replace(/^(?:like|similar to)\s+(?:an?\s+)?/i, '').trim();
  const brand = explicit || queryName || titleName(subject);
  if (!brand || /^(studio|me)$/i.test(brand)) return null;
  const blockedTitle = /^(the work|on this page|studio|open studio)$/i;
  const sectionTitles = titles.filter((title) => !blockedTitle.test(title));
  sentences.forEach((line) => {
    if (sectionTitles.length >= 4) return;
    const cut = line.split(/[,:]/)[0].trim().slice(0, 48);
    if (!cut || blockedTitle.test(cut)) return;
    if (sectionTitles.some((title) => title.toLowerCase() === cut.toLowerCase())) return;
    sectionTitles.push(cut);
  });
  if (!sectionTitles.length) return null;
  const features = sectionTitles.slice(0, 6).map((title, index) => {
    const words = title.toLowerCase().split(/[^a-z0-9]+/).filter((word) => word.length > 3);
    const matched = sentences.find((line) => words.some((word) => line.toLowerCase().includes(word)));
    const follow = sentences[index] && sentences[index] !== matched ? sentences[index] : '';
    const body = [matched, follow].filter(Boolean).join(' ') || about.slice(0, 420);
    return { id: sectionSlug(title, index), title, body: body.slice(0, 480) };
  });
  const dark = Boolean(research?.dark);
  const button = String(research?.button || '').replace(/\s+/g, ' ').trim();
  const cta = button && button.length < 32 && !/^open studio$/i.test(button) ? button : features[0].title;
  return {
    kind: 'studied',
    brand,
    accent: (research?.colors && research.colors[0]) || '#1d4e89',
    ink: dark ? '#f4f1ea' : '#1c1917',
    header: dark ? '#14161c' : '#f7f6f3',
    dark,
    nav: features.map((item) => item.title),
    hero: {
      title: brand,
      lead: (sentences.slice(0, 2).join(' ') || about).slice(0, 420),
      cta,
    },
    features,
    studied: {
      url: research?.url || '',
      imagery: research?.imagery || 'type and color',
      dark,
    },
  };
}

export function siteAfterResearch(brief, query, research) {
  const corrected = correctKnownNameTypos(String(brief || '').trim());
  if (!explicitBrand(corrected) && /^me$/i.test(String(query || '').trim())) return null;
  const name = titleName(query || referenceQuery(corrected));
  if (isSearchName(name) || isSearchName(referenceQuery(corrected)) || wantsSearch(corrected)) {
    const source = searchSource(corrected);
    const brand = titleName(name || referenceQuery(corrected));
    if (isSearchName(brand)) source.brand = brand;
    if (research?.colors?.[0]) source.accent = research.colors[0];
    return source;
  }
  const about = String(research?.about || '').trim();
  const headings = (research?.headings || []).filter(Boolean);
  const nav = (research?.nav || []).filter(Boolean);
  if (about.length < 40 && !headings.length && nav.length < 2) return null;
  return sourceFromResearch(corrected, {
    query: name || research?.query || '',
    about,
    headings,
    nav,
    colors: research?.colors || [],
    dark: Boolean(research?.dark),
    imagery: research?.imagery || 'type and color',
    url: research?.url || '',
    button: research?.button || '',
  });
}

function stripResearchText(value) {
  return String(value || '')
    .replace(/<script[\s\S]*?<\/script>/gi, ' ')
    .replace(/<style[\s\S]*?<\/style>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function researchHex(hex) {
  const raw = String(hex || '').replace('#', '').toLowerCase();
  if (raw.length === 3) return raw.split('').map((ch) => ch + ch).join('');
  return raw.length === 6 ? raw : '';
}

function researchGray(hex) {
  const raw = researchHex(hex);
  if (!raw) return true;
  const r = parseInt(raw.slice(0, 2), 16);
  const g = parseInt(raw.slice(2, 4), 16);
  const b = parseInt(raw.slice(4, 6), 16);
  return Math.max(r, g, b) - Math.min(r, g, b) < 18;
}

function researchDark(hex) {
  const raw = researchHex(hex);
  if (!raw) return false;
  const r = parseInt(raw.slice(0, 2), 16) / 255;
  const g = parseInt(raw.slice(2, 4), 16) / 255;
  const b = parseInt(raw.slice(4, 6), 16) / 255;
  return (0.2126 * r) + (0.7152 * g) + (0.0722 * b) < 0.45;
}

function layoutFromHtml(html) {
  const headings = [...html.matchAll(/<h[12][^>]*>([\s\S]*?)<\/h[12]>/gi)]
    .map((match) => stripResearchText(match[1]))
    .filter((line) => line && line.length < 80)
    .slice(0, 6);
  const navHtml = (html.match(/<nav[\s\S]*?<\/nav>/i) || [''])[0];
  const nav = [...navHtml.matchAll(/<a[^>]*>([\s\S]*?)<\/a>/gi)]
    .map((match) => stripResearchText(match[1]))
    .filter((line) => line && line.length < 28 && !/^(menu|skip|home)$/i.test(line))
    .slice(0, 6);
  const counts = {};
  for (const match of html.matchAll(/#(?:[0-9a-fA-F]{6}|[0-9a-fA-F]{3})\b/g)) {
    const hex = `#${researchHex(match[0])}`;
    if (!hex || hex === '#' || researchGray(hex)) continue;
    counts[hex] = (counts[hex] || 0) + 1;
  }
  const colors = Object.entries(counts).sort((a, b) => b[1] - a[1]).map(([hex]) => hex).slice(0, 4);
  const bg = html.slice(0, 12000).match(/background(?:-color)?\s*:\s*(#[0-9a-fA-F]{3,6})/i);
  const dark = bg ? researchDark(bg[1]) : false;
  const images = (html.match(/<img\b/gi) || []).length;
  const imagery = images > 4 ? 'pictures' : images > 0 ? 'a few images' : 'type and color';
  return { headings, nav, colors, dark, imagery };
}

export async function researchViaPreview(query, origin = '') {
  const name = String(query || '').trim().slice(0, 80);
  if (name.length < 2) return { ok: false, reason: 'missing' };
  const preview = async (url) => {
    const res = await fetch(`${origin}/computer-preview?url=${encodeURIComponent(url)}`, { signal: AbortSignal.timeout(20000) });
    if (!res.ok) throw new Error(String(res.status));
    const html = await res.text();
    if (/This page could not be loaded/i.test(html.slice(0, 500))) throw new Error('blocked');
    return html;
  };
  let about = '';
  try {
    const searchRes = await fetch(`https://en.wikipedia.org/w/api.php?action=query&list=search&srsearch=${encodeURIComponent(name)}&utf8=&format=json&origin=*`);
    const searchJson = await searchRes.json();
    const titles = (searchJson?.query?.search || []).map((item) => item.title).filter(Boolean).slice(0, 5);
    for (const title of titles) {
      const sumRes = await fetch(`https://en.wikipedia.org/api/rest_v1/page/summary/${encodeURIComponent(title)}`);
      if (!sumRes.ok) continue;
      const sum = await sumRes.json();
      const extract = String(sum.extract || '');
      if (sum.type === 'disambiguation' || extract.length < 40) continue;
      about = extract.slice(0, 700);
      break;
    }
  } catch (_) {
    about = '';
  }
  let url = '';
  try {
    const results = await preview(`https://html.duckduckgo.com/html/?q=${encodeURIComponent(`${name} official website`)}`);
    const links = [];
    for (const match of results.matchAll(/href="([^"]+)"/g)) {
      let href = match[1];
      try {
        const parsed = new URL(href, 'http://localhost');
        if (parsed.searchParams.get('url')) href = parsed.searchParams.get('url');
      } catch (_) { /* keep the raw href */ }
      const wrapped = String(href).match(/[?&]uddg=([^&]+)/);
      if (wrapped) {
        try { href = decodeURIComponent(wrapped[1]); } catch (_) { /* keep href */ }
      }
      if (/^https?:\/\//i.test(href)) links.push(href);
    }
    url = links.find((link) => !/wikipedia\.org|facebook\.com|instagram\.com|twitter\.com|linkedin\.com|youtube\.com|duckduckgo\.com/i.test(link)) || '';
  } catch (_) {
    url = '';
  }
  let layout = null;
  if (url) {
    try {
      layout = layoutFromHtml(await preview(url));
    } catch (_) {
      layout = null;
    }
  }
  const structured = layout && ((layout.headings && layout.headings.length) || (layout.nav && layout.nav.length > 1) || (layout.colors && layout.colors.length));
  if (!about || about.length < 40 || !structured) {
    return {
      ok: false,
      reason: 'incomplete',
      query: name,
      about,
      url,
      headings: layout?.headings || [],
      nav: layout?.nav || [],
      colors: layout?.colors || [],
      dark: Boolean(layout?.dark),
      imagery: layout?.imagery || 'type and color',
    };
  }
  return { ok: true, query: name, about, url, ...layout };
}

function parseModelValue(value) {
  if (value && typeof value === 'object') return value;
  const raw = String(value || '').trim();
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch (_) {
    const start = raw.indexOf('{"mode"');
    if (start < 0) return null;
    try {
      return JSON.parse(raw.slice(start));
    } catch (_) {
      return null;
    }
  }
}

export function computerFromModel(value) {
  const parsed = parseModelValue(value);
  if (!parsed || parsed.mode !== 'render') return null;
  const artifacts = parsed.artifacts && typeof parsed.artifacts === 'object' ? parsed.artifacts : {};
  const html = String(artifacts.html || '');
  const css = String(artifacts.css || '');
  const js = String(artifacts.js || '');
  if (!html && !css && !js) return null;
  return {
    chat: COMPUTER_LINE,
    files: [
      { path: 'index.html', language: 'html', content: html },
      { path: 'styles.css', language: 'css', content: css },
      { path: 'app.js', language: 'javascript', content: js },
    ],
  };
}

export function previewFromFiles(files) {
  const byPath = Object.fromEntries(files.map((file) => [file.path, file.content]));
  return {
    html: byPath['index.html'] || '',
    css: byPath['styles.css'] || '',
    js: byPath['app.js'] || '',
  };
}

const ROLE_PREF = {
  plan: ['claude', 'openai', 'gemini'],
  design: ['claude', 'openai', 'gemini'],
  code: ['grok', 'claude', 'openai', 'gemini'],
  status: ['claude', 'gemini', 'openai', 'grok'],
};

function modelFor(provider, task) {
  if (provider === 'claude') return task === 'plan' ? 'claude-opus-4-5' : 'claude-sonnet-4-5';
  if (provider === 'gemini') return 'gemini-flash-latest';
  if (provider === 'grok') return 'grok-code-fast-1';
  if (task === 'plan' || task === 'code') return 'gpt-4o';
  return 'gpt-4o-mini';
}

export function routeModels(providers) {
  const flags = providers || {};
  const available = ['openai', 'claude', 'gemini', 'grok'].filter((id) => flags[id]);
  const pick = (id, task) => {
    const pref = ROLE_PREF[id] || ROLE_PREF.status;
    const provider = pref.find((name) => available.includes(name)) || available[0] || null;
    if (!provider) return { provider: 'unconfigured', model: 'none' };
    return { provider, model: modelFor(provider, task) };
  };
  return {
    plan: pick('plan', 'plan'),
    design: pick('design', 'write'),
    code: pick('code', 'code'),
    status: pick('status', 'chat'),
  };
}

export function handoffSteps(providers) {
  const routed = routeModels(providers);
  return [
    { id: 'plan', title: 'Plan the system', strength: 'Reasoning', why: 'Decides the parts and checks they still agree', ...routed.plan, status: 'running' },
    { id: 'design', title: 'Shape the page', strength: 'Writing', why: 'Sets the words and the look', ...routed.design, status: 'waiting' },
    { id: 'code', title: 'Write the files', strength: 'Code', why: 'Writes every file from the same source', ...routed.code, status: 'waiting' },
  ];
}
